/**
 * Datos simulados para ejecutar la UI sin backend (navegador/dev/test). Reproducen
 * la forma de las proyecciones reales (Fase 3) que servirá el runtime IPC (Fase 5).
 * Contexto: negocio de Mercado Libre en Chile (CLP, IVA 19 %).
 */
import { MockTransport, type Transport } from "./transport";
import { IPC_CHANNELS } from "./channels";
import type {
  ExecutiveOverviewDto, EngineOverviewDto, ProductDevelopmentOverviewDto, OpenInvestigationInput, SimulateInput,
  CredentialsStatus, SaveCredentialsInput,
} from "./types";
import { PdBackend, buildIndices } from "./pd-backend";

/** Almacén de credenciales simulado (dev/navegador): en memoria, sin red real. */
function createMockCredentials() {
  const store = new Map<string, string>();
  const m = (v: string | undefined): string => (v && v.length > 4 ? `${"•".repeat(Math.min(v.length - 4, 12))}${v.slice(-4)}` : v ? "•".repeat(v.length) : "");
  const status = (): CredentialsStatus => ({
    mercadolibre: { configured: store.has("ml.refreshToken"), masked: m(store.get("ml.refreshToken")) },
    ai: { configured: store.has("ai.apiKey"), masked: m(store.get("ai.apiKey")), extra: store.get("ai.provider") ?? "openai" },
    images: { configured: store.has("image.apiKey"), masked: m(store.get("image.apiKey")), extra: store.get("image.provider") ?? "openai" },
  });
  return {
    status,
    save(input: SaveCredentialsInput): CredentialsStatus {
      if (input.provider === "mercadolibre") {
        store.set("ml.clientId", input.values.clientId ?? "");
        store.set("ml.clientSecret", input.values.clientSecret ?? "");
        store.set("ml.refreshToken", input.values.refreshToken ?? "");
      } else if (input.provider === "images") {
        store.set("image.provider", input.values.provider ?? "openai");
        store.set("image.apiKey", input.values.apiKey ?? "");
      } else {
        store.set("ai.provider", input.values.provider ?? "openai");
        store.set("ai.apiKey", input.values.apiKey ?? "");
      }
      return status();
    },
    test(provider: string): { ok: boolean; detail: string } {
      const configured = provider === "mercadolibre" ? store.has("ml.refreshToken") : provider === "images" ? store.has("image.apiKey") : store.has("ai.apiKey");
      return configured
        ? { ok: true, detail: "Conexión simulada correcta (modo demostración, sin llamada real)" }
        : { ok: false, detail: "Faltan credenciales" };
    },
    mlAccount() {
      return store.has("ml.refreshToken")
        ? { connected: true, detail: "Cuenta sincronizada (demostración)", nickname: "TU_CUENTA_ML", siteId: "MLC", userId: "123456", items: 5 }
        : { connected: false, detail: "Configura las credenciales de Mercado Libre en Seguridad" };
    },
    aiAsk(prompt: string) {
      const provider = store.get("ai.provider") ?? "openai";
      return store.has("ai.apiKey")
        ? { ok: true, answer: `(demostración) Recibí: "${prompt}". Con credenciales reales, ${provider} responderá de verdad.`, provider }
        : { ok: false, answer: "Configura tu API key de IA en Seguridad.", provider };
    },
    // Devuelve el valor crudo (MockTransport lo envuelve en {ok,result}); lanza para errores de validación.
    mlBeginConnect(input: { clientId: string; clientSecret: string; redirectUri: string; authBase?: string }): { authUrl: string } {
      const clientId = (input.clientId ?? "").trim(), clientSecret = (input.clientSecret ?? "").trim(), redirectUri = (input.redirectUri ?? "").trim();
      if (!clientId || !clientSecret || !redirectUri) throw new Error("Completa App ID, Secret y URL de redirección");
      if (!/^https?:\/\//i.test(redirectUri)) throw new Error("La URL de redirección debe empezar con http(s)://");
      store.set("ml.clientId", clientId); store.set("ml.clientSecret", clientSecret); store.set("ml.redirectUri", redirectUri);
      const base = (input.authBase ?? "").trim() || "https://auth.mercadolibre.cl/authorization";
      const q = new URLSearchParams({ response_type: "code", client_id: clientId, redirect_uri: redirectUri });
      return { authUrl: `${base}?${q.toString()}` };
    },
    mlCompleteConnect(code: string): { ok: boolean; detail: string } {
      const raw = (code ?? "").trim();
      if (!raw) return { ok: false, detail: "Pega el código (o la URL de redirección) que te dio Mercado Libre" };
      if (!store.has("ml.clientId")) return { ok: false, detail: "Primero inicia la conexión (paso 1)" };
      // Demostración: simula el canje exitoso guardando un refresh token de ejemplo.
      store.set("ml.refreshToken", "demo-refresh-token");
      return { ok: true, detail: "(demostración) Conectado: con datos reales, Mercado Libre canjearía el código por tu refresh token." };
    },
    mlQuestions() {
      if (!store.has("ml.refreshToken")) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", questions: [] };
      return {
        ok: true, detail: "2 preguntas sin responder (demostración)",
        questions: [
          { id: "q-1001", itemId: "MLC1001", text: "Hola, ¿tienen stock para envío inmediato a Santiago?", date: "2026-08-14T10:00:00Z" },
          { id: "q-1002", itemId: "MLC1002", text: "¿El aro de luz incluye el trípode o se compra aparte?", date: "2026-08-15T09:30:00Z" },
        ],
      };
    },
    draftQuestions() {
      if (!store.has("ml.refreshToken")) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", drafts: [] };
      if (!store.has("ai.apiKey")) return { ok: false, detail: "Configura tu API key de IA en Seguridad.", drafts: [] };
      const q = this.mlQuestions().questions;
      const drafts = q.map((it) => ({
        id: it.id, itemId: it.itemId, question: it.text,
        draft: `¡Hola! Gracias por tu consulta. (demostración) Con tus credenciales reales, la IA redacta aquí una respuesta concreta a: "${it.text}". Si falta un dato, lo marca como [COMPLETAR: ...].`,
      }));
      return { ok: true, detail: `${drafts.length} respuesta(s) redactada(s) — borrador, no enviadas.`, drafts };
    },
    answerQuestion(input: { questionId?: string; text?: string }) {
      const text = (input.text ?? "").trim();
      if (!(input.questionId ?? "").trim()) return { ok: false, detail: "Falta la pregunta a responder." };
      if (!text) return { ok: false, detail: "La respuesta no puede estar vacía." };
      if (!store.has("ml.refreshToken")) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad" };
      return { ok: true, detail: "(demostración) Respuesta enviada. Con tus credenciales reales, se publica en tu pregunta de Mercado Libre." };
    },
    analyzeImage() {
      if (!store.has("ai.apiKey")) return { ok: false, category: "", images: [], detail: "Configura tu API key de IA (Anthropic) en Seguridad." };
      const slots = [
        { n: 1, title: "Portada principal", purpose: "Fondo blanco puro, producto centrado, sin texto." },
        { n: 2, title: "Ángulo alternativo / detalle", purpose: "Otro ángulo o zoom a un detalle clave." },
        { n: 3, title: "Infografía de características", purpose: "3-4 etiquetas de beneficios, ficha técnica visual." },
        { n: 4, title: "Producto en contexto de uso", purpose: "Escena lifestyle realista, luz natural." },
        { n: 5, title: "Escala / contenido del paquete", purpose: "Dimensiones o 'qué incluye'." },
        { n: 6, title: "Confianza / garantía", purpose: "Sellos de confianza, tonos azules/verdes." },
      ];
      return { ok: true, category: "Producto (demostración)", detail: "Categoría detectada (demostración). Con tu key de Anthropic, Claude analiza tu foto real.",
        images: slots.map((s) => ({ ...s, prompt: `(demostración) Prompt optimizado para "${s.title}" de tu producto, cumpliendo estándares de Mercado Libre.` })) };
    },
    generateImage(prompt: string) {
      if (!store.has("image.apiKey")) return { ok: false, imageBase64: "", detail: "Configura tu API key de imágenes (OpenAI) en Seguridad." };
      const label = (prompt || "").slice(0, 40).replace(/[<>&]/g, "");
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><rect width='100%' height='100%' fill='#0d0d0f'/><rect x='16' y='16' width='368' height='368' rx='16' fill='#12203a'/><text x='50%' y='44%' fill='#f2c14e' font-size='20' font-family='sans-serif' text-anchor='middle'>Imagen (demostración)</text><text x='50%' y='54%' fill='#9aa4b2' font-size='12' font-family='sans-serif' text-anchor='middle'>Con tu key de OpenAI se genera real</text><text x='50%' y='62%' fill='#5b6474' font-size='10' font-family='sans-serif' text-anchor='middle'>${label}</text></svg>`;
      const b64 = btoa(unescape(encodeURIComponent(svg)));
      return { ok: true, imageBase64: `data:image/svg+xml;base64,${b64}`, detail: "Imagen generada (demostración)." };
    },
    generateDescription(input: { title?: string; context?: string }) {
      if (!store.has("ai.apiKey")) return { ok: false, text: "", detail: "Configura tu API key de IA en Seguridad." };
      const title = (input.title ?? "").trim();
      if (!title) return { ok: false, text: "", detail: "Ingresa el título del producto." };
      const text = [
        `¿Cansado de opciones que prometen y no cumplen? Esto es distinto.`,
        ``,
        `**${title}** — pensado para quienes buscan calidad real y sin complicaciones.`,
        ``,
        `✅ Fácil de usar: lo aprovechás desde el primer día.`,
        `✅ Materiales resistentes: pensado para durar.`,
        `✅ Diseño práctico: se adapta a tu día a día.`,
        ``,
        `¿Dudás de la calidad? Cada unidad se revisa antes de enviarse.`,
        `¿Y el envío? Despachamos rápido y con seguimiento.`,
        ``,
        `🛡️ Garantía: [COMPLETAR: garantía]`,
        ``,
        `Sumalo hoy y empezá a disfrutarlo — es la opción lógica y sin riesgo.`,
        ``,
        `📋 FICHA TÉCNICA`,
        `• Medidas: [COMPLETAR: medidas]`,
        `• Material: [COMPLETAR: material]`,
        `• Contenido: [COMPLETAR: contenido del paquete]`,
        ``,
        `(Demostración: con tu key de Anthropic, Claude redacta una descripción real y persuasiva a partir del título y tus datos.)`,
      ].join("\n");
      return { ok: true, text, detail: "Descripción generada (demostración)." };
    },
    onboardingStatus(): { done: boolean } { return { done: store.get("app.onboardingDone") === "1" }; },
    completeOnboarding(): { done: boolean } { store.set("app.onboardingDone", "1"); return { done: true }; },
    businessGet() {
      const iva = store.has("biz.ivaPercent") ? Number(store.get("biz.ivaPercent")) : 19;
      return {
        businessName: store.get("biz.name") ?? "",
        rut: store.get("biz.rut") ?? "",
        ivaPercent: Number.isFinite(iva) ? iva : 19,
        currency: store.get("biz.currency") ?? "CLP",
        mlUserId: store.get("biz.mlUserId") ?? "",
      };
    },
    syncGet() {
      const min = store.has("sync.intervalMinutes") ? Number(store.get("sync.intervalMinutes")) : 30;
      return { enabled: store.get("sync.enabled") === "1", intervalMinutes: Number.isFinite(min) ? min : 30 };
    },
    syncSave(input: Partial<{ enabled: boolean; intervalMinutes: number }>) {
      const next = { ...this.syncGet(), ...input };
      if (!Number.isFinite(next.intervalMinutes) || next.intervalMinutes < 5 || next.intervalMinutes > 720) throw new Error("El intervalo debe estar entre 5 y 720 minutos");
      store.set("sync.enabled", next.enabled ? "1" : "0");
      store.set("sync.intervalMinutes", String(next.intervalMinutes));
      return this.syncGet();
    },
    businessSave(input: Partial<{ businessName: string; rut: string; ivaPercent: number; currency: string; mlUserId: string }>) {
      const cur = this.businessGet();
      const next = { ...cur, ...input };
      if (!Number.isFinite(next.ivaPercent) || next.ivaPercent < 0 || next.ivaPercent > 100) throw new Error("El IVA debe ser un porcentaje entre 0 y 100");
      const currency = (next.currency ?? "").trim().toUpperCase();
      if (currency.length !== 3) throw new Error("La moneda debe ser un código ISO de 3 letras (ej: CLP)");
      store.set("biz.name", (next.businessName ?? "").trim());
      store.set("biz.rut", (next.rut ?? "").trim());
      store.set("biz.ivaPercent", String(next.ivaPercent));
      store.set("biz.currency", currency);
      store.set("biz.mlUserId", (next.mlUserId ?? "").trim());
      return this.businessGet();
    },
  };
}

export const EXECUTIVE_OVERVIEW: ExecutiveOverviewDto = {
  overallScore: 92,
  status: "healthy",
  headline: "El ecosistema opera con salud. 3 decisiones esperan tu visto bueno.",
  kpis: [
    { key: "revenue", label: "Ventas confirmadas", value: "$48.250.900", deltaPct: 12.4, accent: "yellow", icon: "sales" },
    { key: "orders", label: "Órdenes confirmadas", value: "1.284", deltaPct: 8.1, accent: "blue", icon: "marketplace" },
    { key: "margin", label: "Margen neto", value: "31,6%", deltaPct: 2.3, accent: "green", icon: "finance" },
    { key: "stock", label: "Stock vendible", value: "$21.905.400", deltaPct: -4.7, accent: "red", icon: "inventory" },
  ],
  engines: [
    { id: "product", name: "Producto", status: "healthy", score: 98, uptimePct: 99.9 },
    { id: "marketplace", name: "Marketplace", status: "healthy", score: 95, uptimePct: 99.7 },
    { id: "inventory", name: "Inventario", status: "degraded", score: 78, uptimePct: 98.2 },
    { id: "sales", name: "Ventas", status: "healthy", score: 96, uptimePct: 99.8 },
    { id: "pricing", name: "Precios", status: "healthy", score: 94, uptimePct: 99.6 },
    { id: "purchase", name: "Compras", status: "healthy", score: 91, uptimePct: 99.4 },
    { id: "supplier", name: "Proveedores", status: "healthy", score: 90, uptimePct: 99.5 },
    { id: "logistics", name: "Logística", status: "degraded", score: 82, uptimePct: 98.9 },
    { id: "customer", name: "Clientes", status: "healthy", score: 93, uptimePct: 99.7 },
    { id: "analytics", name: "Analytics", status: "healthy", score: 97, uptimePct: 99.9 },
    { id: "finance", name: "Finanzas", status: "healthy", score: 95, uptimePct: 99.8 },
    { id: "ai", name: "IA", status: "healthy", score: 99, uptimePct: 99.9 },
  ],
  decisions: [
    {
      id: "dec-1",
      title: "Reponer 3 SKU de alta rotación antes del quiebre",
      rationale: "Cobertura proyectada < 6 días para 3 productos con demanda creciente.",
      priority: "high",
      confidence: 91,
      engine: "Inventario",
    },
    {
      id: "dec-2",
      title: "Bajar 6% el precio de 12 publicaciones estancadas",
      rationale: "Elasticidad estimada permite recuperar Buy Box sin perder margen objetivo.",
      priority: "medium",
      confidence: 84,
      engine: "Precios",
    },
    {
      id: "dec-3",
      title: "Cambiar de transportista en zona norte",
      rationale: "SLA de entrega cayó a 82%; alternativa mejora costo y tiempo estimado.",
      priority: "critical",
      confidence: 88,
      engine: "Logística",
    },
  ],
  recommendations: [
    { id: "rec-1", title: "Categoría 'Hogar' con demanda al alza", impact: "+$3,2M potencial / mes", kind: "opportunity" },
    { id: "rec-2", title: "2 proveedores concentran el 61% del costo", impact: "Riesgo de dependencia", kind: "threat" },
    { id: "rec-3", title: "Consolidar despachos vespertinos", impact: "-11% costo logístico", kind: "optimization" },
  ],
  goals: [
    { id: "goal-1", label: "Meta de ventas del trimestre", progress: 74, target: "$150M" },
    { id: "goal-2", label: "Margen neto ≥ 30%", progress: 88, target: "30%" },
    { id: "goal-3", label: "Reputación MercadoLíder Platinum", progress: 96, target: "Platinum" },
  ],
  revenueTrend: [28, 31, 30, 35, 33, 39, 41, 44, 42, 47, 49, 52, 48],
};

const ENGINE_OVERVIEWS: Record<string, EngineOverviewDto> = {
  finance: {
    engineId: "finance",
    title: "Finanzas",
    metrics: [
      { label: "Ingresos (30d)", value: "$48.250.900", deltaPct: 12.4, accent: "green", icon: "arrow-up" },
      { label: "Egresos (30d)", value: "$32.980.100", deltaPct: 6.2, accent: "red", icon: "finance" },
      { label: "IVA débito", value: "$9.167.671", accent: "yellow", icon: "finance" },
      { label: "Flujo de caja", value: "$15.270.800", deltaPct: 9.8, accent: "blue", icon: "pulse" },
    ],
    columns: ["Documento", "Contraparte", "Estado", "Monto"],
    rows: [
      { id: "f1", primary: "Factura #FIN-1042", secondary: "Cliente mayorista Ltda.", amount: "$2.380.000", status: { label: "Pagada", tone: "success" } },
      { id: "f2", primary: "Factura #FIN-1043", secondary: "Distribuidora Andes", amount: "$1.190.500", status: { label: "Emitida", tone: "info" } },
      { id: "f3", primary: "Compra #PO-0781", secondary: "Proveedor TechImport", amount: "$4.560.000", status: { label: "Por pagar", tone: "warning" } },
      { id: "f4", primary: "Factura #FIN-1040", secondary: "Retail Sur SpA", amount: "$860.000", status: { label: "Vencida", tone: "danger" } },
    ],
  },
  sales: {
    engineId: "sales",
    title: "Ventas",
    metrics: [
      { label: "Órdenes (30d)", value: "1.284", deltaPct: 8.1, accent: "yellow", icon: "sales" },
      { label: "Ticket promedio", value: "$37.580", deltaPct: 3.4, accent: "green", icon: "arrow-up" },
      { label: "Tasa conversión", value: "4,9%", deltaPct: 0.6, accent: "blue", icon: "target" },
      { label: "Devoluciones", value: "1,8%", deltaPct: -0.4, accent: "red", icon: "alert" },
    ],
    columns: ["Orden", "Cliente", "Estado", "Total"],
    rows: [
      { id: "s1", primary: "Orden #SO-9021", secondary: "camila.rojas", amount: "$54.990", status: { label: "Despachada", tone: "info" } },
      { id: "s2", primary: "Orden #SO-9022", secondary: "j.morales", amount: "$21.500", status: { label: "Pagada", tone: "success" } },
      { id: "s3", primary: "Orden #SO-9023", secondary: "tienda_norte", amount: "$132.000", status: { label: "Preparando", tone: "warning" } },
      { id: "s4", primary: "Orden #SO-9024", secondary: "p.fuentes", amount: "$8.990", status: { label: "Completada", tone: "brand" } },
    ],
  },
};

const GENERIC_ROWS = (title: string): EngineOverviewDto["rows"] => [
  { id: "g1", primary: `${title} · Registro 001`, secondary: "Actualizado hoy", amount: "—", status: { label: "Activo", tone: "success" } },
  { id: "g2", primary: `${title} · Registro 002`, secondary: "Actualizado ayer", amount: "—", status: { label: "En revisión", tone: "warning" } },
  { id: "g3", primary: `${title} · Registro 003`, secondary: "Hace 3 días", amount: "—", status: { label: "Sincronizado", tone: "info" } },
];

const ENGINE_TITLES: Record<string, string> = {
  product: "Producto", marketplace: "Marketplace", inventory: "Inventario", pricing: "Precios",
  purchase: "Compras", supplier: "Proveedores", logistics: "Logística", customer: "Clientes",
  analytics: "Analytics", ai: "IA",
};

export function engineOverviewFor(engineId: string): EngineOverviewDto {
  const preset = ENGINE_OVERVIEWS[engineId];
  if (preset) return preset;
  const title = ENGINE_TITLES[engineId] ?? engineId;
  return {
    engineId,
    title,
    metrics: [
      { label: "Registros", value: "3.420", deltaPct: 5.2, accent: "yellow", icon: "spark" },
      { label: "Salud del motor", value: "97%", accent: "green", icon: "pulse" },
      { label: "Eventos (24h)", value: "812", deltaPct: 11.0, accent: "blue", icon: "bolt" },
      { label: "Alertas", value: "2", accent: "red", icon: "alert" },
    ],
    columns: ["Elemento", "Detalle", "Estado", ""],
    rows: GENERIC_ROWS(title),
  };
}

const indices = buildIndices;

/** Mock con estado del motor Product para dev/navegador (CRUD que persiste en sesión). */
function createMockProductStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { active: "success", draft: "warning", inactive: "info", archived: "neutral" };
  const LABEL: Record<string, string> = { active: "Activo", draft: "Borrador", inactive: "Inactivo", archived: "Archivado" };
  type P = { id: string; sku: string; name: string; unit: string; status: string };
  let seq = 0;
  const items: P[] = [
    { id: "p1", sku: "ARO-LED-26", name: "Aro de luz LED 26cm con trípode", unit: "unit", status: "active" },
    { id: "p2", sku: "ORG-ACR-01", name: "Organizador de maquillaje acrílico", unit: "unit", status: "active" },
    { id: "p3", sku: "CARG-3EN1", name: "Cargador inalámbrico 3 en 1", unit: "unit", status: "draft" },
  ];
  return {
    overview(): EngineOverviewDto {
      const count = (s: string): number => items.filter((p) => p.status === s).length;
      return {
        engineId: "product", title: "Producto",
        metrics: [
          { label: "Productos", value: String(items.length), accent: "yellow", icon: "product" },
          { label: "Activos", value: String(count("active")), accent: "green", icon: "check" },
          { label: "Borradores", value: String(count("draft")), accent: "blue", icon: "spark" },
          { label: "Archivados", value: String(count("archived")), accent: "red", icon: "alert" },
        ],
        columns: ["Producto", "Detalle", "Estado", "Unidad"],
        rows: items.map((p) => ({ id: p.id, primary: p.name, secondary: `SKU ${p.sku}`, amount: p.unit, status: { label: LABEL[p.status] ?? p.status, tone: TONE[p.status] ?? "neutral" } })),
      };
    },
    create(input: { sku?: string; name?: string; unit?: string }) {
      if (!input.sku?.trim() || !input.name?.trim()) return { ok: false, message: "SKU y nombre son obligatorios" };
      const id = `p-new-${++seq}`;
      items.unshift({ id, sku: input.sku.trim(), name: input.name.trim(), unit: input.unit?.trim() || "unit", status: "draft" });
      return { ok: true, id };
    },
    setStatus(id: string, action: "activate" | "deactivate" | "archive") {
      const p = items.find((x) => x.id === id);
      if (!p) return { ok: false, message: "Producto no encontrado" };
      p.status = action === "activate" ? "active" : action === "deactivate" ? "inactive" : "archived";
      return { ok: true, id };
    },
    catalog() {
      return { items: items.filter((p) => p.status !== "archived").map((p) => ({ sku: p.sku, name: p.name, unit: p.unit })) };
    },
  };
}
type EngineRowDtoLite = { status: { tone: "neutral" | "success" | "warning" | "danger" | "info" | "brand" } };

/** Mock con estado del motor Customer para dev/navegador (CRUD que persiste en sesión). */
function createMockCustomerStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { active: "success", inactive: "info", draft: "warning", archived: "neutral" };
  const LABEL: Record<string, string> = { active: "Activo", inactive: "Inactivo", draft: "Borrador", archived: "Archivado" };
  const TYPE: Record<string, string> = { retail: "Minorista", corporate: "Corporativo" };
  type C = { id: string; code: string; name: string; type: string; status: string };
  let seq = 0;
  const items: C[] = [
    { id: "c1", code: "CLI-001", name: "Camila Rojas", type: "retail", status: "active" },
    { id: "c2", code: "CLI-002", name: "Tienda Norte SpA", type: "corporate", status: "active" },
    { id: "c3", code: "CLI-004", name: "Retail Sur SpA", type: "corporate", status: "inactive" },
    { id: "c4", code: "CLI-005", name: "Pedro Fuentes", type: "retail", status: "draft" },
  ];
  return {
    overview(): EngineOverviewDto {
      return {
        engineId: "customer", title: "Clientes",
        metrics: [
          { label: "Clientes", value: String(items.length), accent: "yellow", icon: "customer" },
          { label: "Activos", value: String(items.filter((c) => c.status === "active").length), accent: "green", icon: "check" },
          { label: "Corporativos", value: String(items.filter((c) => c.type === "corporate").length), accent: "blue", icon: "supplier" },
          { label: "Inactivos", value: String(items.filter((c) => c.status === "inactive").length), accent: "red", icon: "pulse" },
        ],
        columns: ["Cliente", "Detalle", "Estado", ""],
        rows: items.map((c) => ({ id: c.id, primary: c.name, secondary: `${c.code} · ${TYPE[c.type] ?? c.type}`, amount: "—", status: { label: LABEL[c.status] ?? c.status, tone: TONE[c.status] ?? "neutral" } })),
      };
    },
    create(input: { code?: string; name?: string; type?: string }) {
      if (!input.code?.trim() || !input.name?.trim()) return { ok: false, message: "Código y nombre son obligatorios" };
      const id = `c-new-${++seq}`;
      items.unshift({ id, code: input.code.trim(), name: input.name.trim(), type: input.type?.trim() || "retail", status: "draft" });
      return { ok: true, id };
    },
    setStatus(id: string, action: "activate" | "deactivate" | "archive") {
      const c = items.find((x) => x.id === id);
      if (!c) return { ok: false, message: "Cliente no encontrado" };
      c.status = action === "activate" ? "active" : action === "deactivate" ? "inactive" : "archived";
      return { ok: true, id };
    },
  };
}

/** Mock con estado del motor Supplier para dev/navegador (CRUD que persiste en sesión). */
function createMockSupplierStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { active: "success", inactive: "info", draft: "warning", archived: "neutral" };
  const LABEL: Record<string, string> = { active: "Activo", inactive: "Inactivo", draft: "Borrador", archived: "Archivado" };
  const TYPE: Record<string, string> = { manufacturer: "Fabricante", distributor: "Distribuidor", wholesaler: "Mayorista", importer: "Importador", service_provider: "Servicios" };
  type S = { id: string; code: string; name: string; type: string; country: string; status: string; blacklisted: boolean };
  let seq = 0;
  const items: S[] = [
    { id: "sp1", code: "PROV-TECH", name: "TechImport Ltda.", type: "importer", country: "CL", status: "active", blacklisted: false },
    { id: "sp2", code: "PROV-ANDES", name: "Distribuidora Andes", type: "distributor", country: "CL", status: "active", blacklisted: false },
    { id: "sp3", code: "PROV-CHINA", name: "Shenzhen Trading Co.", type: "manufacturer", country: "CN", status: "active", blacklisted: true },
    { id: "sp4", code: "PROV-LOCAL", name: "Mayorista Central", type: "wholesaler", country: "CL", status: "inactive", blacklisted: false },
  ];
  return {
    overview(): EngineOverviewDto {
      return {
        engineId: "supplier", title: "Proveedores",
        metrics: [
          { label: "Proveedores", value: String(items.length), accent: "yellow", icon: "supplier" },
          { label: "Activos", value: String(items.filter((s) => s.status === "active" && !s.blacklisted).length), accent: "green", icon: "check" },
          { label: "En lista negra", value: String(items.filter((s) => s.blacklisted).length), accent: "red", icon: "shield" },
          { label: "Inactivos", value: String(items.filter((s) => s.status === "inactive").length), accent: "blue", icon: "pulse" },
        ],
        columns: ["Proveedor", "Detalle", "Estado", ""],
        rows: items.map((s) => ({
          id: s.id, primary: s.name, secondary: `${s.code} · ${TYPE[s.type] ?? s.type} · ${s.country}`, amount: "—",
          status: s.blacklisted ? { label: "En lista negra", tone: "danger" } : { label: LABEL[s.status] ?? s.status, tone: TONE[s.status] ?? "neutral" },
        })),
      };
    },
    create(input: { code?: string; name?: string; type?: string; country?: string }) {
      if (!input.code?.trim() || !input.name?.trim()) return { ok: false, message: "Código y nombre son obligatorios" };
      const id = `sp-new-${++seq}`;
      items.unshift({ id, code: input.code.trim(), name: input.name.trim(), type: input.type?.trim() || "distributor", country: input.country?.trim() || "CL", status: "draft", blacklisted: false });
      return { ok: true, id };
    },
    setStatus(id: string, action: "activate" | "deactivate" | "archive" | "blacklist") {
      const s = items.find((x) => x.id === id);
      if (!s) return { ok: false, message: "Proveedor no encontrado" };
      if (action === "blacklist") s.blacklisted = true;
      else s.status = action === "activate" ? "active" : action === "deactivate" ? "inactive" : "archived";
      return { ok: true, id };
    },
  };
}

/** Mock con estado del motor Sales para dev/navegador (crea órdenes que persisten en sesión). */
function createMockSalesStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { confirmed: "success", draft: "warning", invoiced: "info", shipped: "info", completed: "brand", cancelled: "danger" };
  const LABEL: Record<string, string> = { confirmed: "Confirmada", draft: "Borrador", invoiced: "Facturada", shipped: "Despachada", completed: "Completada", cancelled: "Cancelada" };
  const CUSTOMERS: readonly { code: string; name: string }[] = [
    { code: "CLI-001", name: "Camila Rojas" },
    { code: "CLI-002", name: "Tienda Norte SpA" },
  ];
  type O = { id: string; number: string; customerCode: string; total: number; status: string; sku?: string; qty?: number; at: number };
  const money = (v: number): string => `$${Math.round(v).toLocaleString("es-CL")}`;
  const withTax = (net: number): number => net + Math.floor(net * 0.19);
  const daysAgo = (n: number): number => Date.now() - n * 24 * 60 * 60 * 1000;
  let seq = 0;
  const items: O[] = [
    { id: "o1", number: "SO-0001", customerCode: "CLI-001", total: withTax(54990 * 2), status: "confirmed", at: daysAgo(1) },
    { id: "o2", number: "SO-0002", customerCode: "CLI-001", total: withTax(21500), status: "confirmed", at: daysAgo(5) },
    { id: "o3", number: "SO-0003", customerCode: "CLI-002", total: withTax(32990 * 3), status: "draft", at: daysAgo(20) },
    { id: "o4", number: "SO-0004", customerCode: "CLI-002", total: withTax(18990), status: "cancelled", at: daysAgo(45) },
  ];
  return {
    overview(range?: { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number }): EngineOverviewDto {
      const inWin = (o: O, f: number, t: number): boolean => o.at >= f && o.at <= t;
      const cur = range ? items.filter((o) => inWin(o, range.fromEpoch, range.toEpoch)) : items;
      const prev = range ? items.filter((o) => inWin(o, range.prevFromEpoch, range.prevToEpoch)) : [];
      const conf = (a: O[]): O[] => a.filter((o) => o.status === "confirmed");
      const rev = (a: O[]): number => conf(a).reduce((s, o) => s + o.total, 0);
      const canc = (a: O[]): number => a.filter((o) => o.status === "cancelled").length;
      const delta = (c: number, p: number): number | undefined => (range && p > 0 ? Math.round(((c - p) / p) * 1000) / 10 : undefined);
      const dset = (c: number, p: number): { deltaPct?: number } => { const d = delta(c, p); return d === undefined ? {} : { deltaPct: d }; };
      return {
        engineId: "sales", title: "Ventas",
        metrics: [
          { label: "Órdenes", value: String(cur.length), accent: "yellow", icon: "sales", ...dset(cur.length, prev.length) },
          { label: "Confirmadas", value: String(conf(cur).length), accent: "green", icon: "check", ...dset(conf(cur).length, conf(prev).length) },
          { label: "Ingreso confirmado", value: money(rev(cur)), accent: "blue", icon: "finance", ...dset(rev(cur), rev(prev)) },
          { label: "Canceladas", value: String(canc(cur)), accent: "red", icon: "alert", ...dset(canc(cur), canc(prev)) },
        ],
        columns: ["Orden", "Cliente", "Estado", "Total"],
        rows: cur.map((o) => ({ id: o.id, primary: `Orden ${o.number}`, secondary: o.customerCode, amount: money(o.total), status: { label: LABEL[o.status] ?? o.status, tone: TONE[o.status] ?? "neutral" }, at: o.at })),
        ...(range ? { period: { fromEpoch: range.fromEpoch, toEpoch: range.toEpoch } } : {}),
      };
    },
    customers() { return { items: CUSTOMERS.map((c) => ({ code: c.code, name: c.name })) }; },
    create(input: { customerCode?: string; sku?: string; unitPriceMinor?: number; quantity?: number }) {
      if (!input.customerCode?.trim()) return { ok: false, message: "Debes elegir un cliente" };
      if (!input.sku?.trim()) return { ok: false, message: "Debes elegir un producto" };
      if (!(Number(input.unitPriceMinor) > 0)) return { ok: false, message: "El precio debe ser mayor que 0" };
      if (!(Number(input.quantity) > 0)) return { ok: false, message: "La cantidad debe ser mayor que 0" };
      const id = `o-new-${++seq}`;
      const qty = Math.round(Number(input.quantity));
      const net = Math.round(Number(input.unitPriceMinor)) * qty;
      items.unshift({ id, number: `SO-${String(1000 + seq).padStart(4, "0")}`, customerCode: input.customerCode.trim(), total: withTax(net), status: "draft", sku: input.sku.trim(), qty, at: Date.now() });
      return { ok: true, id };
    },
    orderLines(id: string): { sku: string; quantity: number }[] {
      const o = items.find((x) => x.id === id);
      return o && o.sku && o.qty ? [{ sku: o.sku, quantity: o.qty }] : [];
    },
    orderFinance(id: string): { party: string; net: number } | null {
      const o = items.find((x) => x.id === id);
      return o ? { party: o.customerCode, net: Math.round(o.total / 1.19) } : null;
    },
    setStatus(id: string, action: "confirm" | "cancel") {
      const o = items.find((x) => x.id === id);
      if (!o) return { ok: false, message: "Orden no encontrada" };
      if (action === "confirm" && o.status !== "draft") return { ok: false, message: "Sólo se confirman órdenes en borrador" };
      if (action === "cancel" && o.status === "completed") return { ok: false, message: "No se cancela una orden completada" };
      o.status = action === "confirm" ? "confirmed" : "cancelled";
      return { ok: true, id };
    },
  };
}

/** Mock con estado del motor Inventory para dev/navegador (stock y movimientos que persisten en sesión). */
function createMockInventoryStore() {
  const LOW = 10;
  const status = (available: number): EngineOverviewDto["rows"][number]["status"] =>
    available <= 0 ? { label: "Sin stock", tone: "danger" }
      : available < LOW ? { label: "Stock bajo", tone: "warning" }
        : { label: "Disponible", tone: "success" };
  type I = { id: string; sku: string; onHand: number; reserved: number };
  let seq = 0;
  const items: I[] = [
    { id: "i1", sku: "ARO-LED-26", onHand: 40, reserved: 8 },
    { id: "i2", sku: "ORG-ACR-01", onHand: 60, reserved: 5 },
    { id: "i3", sku: "TERM-500", onHand: 6, reserved: 0 },
    { id: "i4", sku: "CARG-3EN1", onHand: 0, reserved: 0 },
    { id: "i5", sku: "FUN-GEN-01", onHand: 120, reserved: 20 },
  ];
  const nf = (v: number): string => v.toLocaleString("es-CL");
  return {
    overview(): EngineOverviewDto {
      const totalOnHand = items.reduce((a, i) => a + i.onHand, 0);
      const totalReserved = items.reduce((a, i) => a + i.reserved, 0);
      const outOfStock = items.filter((i) => i.onHand - i.reserved <= 0).length;
      return {
        engineId: "inventory", title: "Inventario",
        metrics: [
          { label: "SKU en bodega", value: String(items.length), accent: "yellow", icon: "inventory" },
          { label: "Unidades en stock", value: nf(totalOnHand), accent: "green", icon: "check" },
          { label: "Reservadas", value: nf(totalReserved), accent: "blue", icon: "pulse" },
          { label: "Sin stock", value: String(outOfStock), accent: "red", icon: "alert" },
        ],
        columns: ["SKU", "Detalle", "Estado", "On hand"],
        rows: items.map((i) => ({
          id: i.id, primary: i.sku, secondary: `Disp. ${i.onHand - i.reserved} · Reserv. ${i.reserved}`,
          amount: `${nf(i.onHand)} u`, status: status(i.onHand - i.reserved),
        })),
      };
    },
    create(input: { sku?: string; initialOnHand?: number }) {
      if (!input.sku?.trim()) return { ok: false, message: "El SKU es obligatorio" };
      if (!(Number(input.initialOnHand) >= 0)) return { ok: false, message: "La existencia inicial no puede ser negativa" };
      if (items.some((i) => i.sku === input.sku!.trim())) return { ok: false, message: "Ya existe inventario para ese SKU en esa ubicación" };
      const id = `i-new-${++seq}`;
      items.unshift({ id, sku: input.sku.trim(), onHand: Math.round(Number(input.initialOnHand)), reserved: 0 });
      return { ok: true, id };
    },
    move(id: string, action: "receive" | "issue" | "adjust", quantity: number) {
      const i = items.find((x) => x.id === id);
      if (!i) return { ok: false, message: "Inventario no encontrado" };
      const qty = Math.round(Number(quantity));
      if (action !== "adjust" && !(qty > 0)) return { ok: false, message: "La cantidad debe ser mayor que 0" };
      if (action === "receive") i.onHand += qty;
      else if (action === "issue") {
        if (qty > i.onHand - i.reserved) return { ok: false, message: "No hay stock disponible suficiente" };
        i.onHand -= qty;
      } else {
        if (!(qty >= 0)) return { ok: false, message: "El stock ajustado no puede ser negativo" };
        if (qty < i.reserved) return { ok: false, message: "No se puede ajustar por debajo de lo reservado" };
        i.onHand = qty;
      }
      return { ok: true, id };
    },
    // Coherencia Fase 3: efectos de stock por SKU (los dispara Ventas/Compras).
    receiveBySku(sku: string, qty: number) {
      const n = Math.round(Number(qty));
      if (!(n > 0)) return;
      const i = items.find((x) => x.sku === sku);
      if (i) i.onHand += n;
      else items.unshift({ id: `i-auto-${++seq}`, sku, onHand: n, reserved: 0 });
    },
    issueBySku(sku: string, qty: number) {
      const n = Math.round(Number(qty));
      if (!(n > 0)) return;
      const i = items.find((x) => x.sku === sku);
      if (!i) return;
      i.onHand -= Math.min(n, i.onHand - i.reserved);
    },
    availableForSku(sku: string): number {
      const i = items.find((x) => x.sku === sku);
      return i ? i.onHand - i.reserved : 0;
    },
  };
}

/** Mock con estado del motor Purchase para dev/navegador (crea OC que persisten en sesión). */
function createMockPurchaseStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { approved: "success", draft: "warning", partially_received: "info", received: "brand", closed: "neutral", cancelled: "danger" };
  const LABEL: Record<string, string> = { approved: "Aprobada", draft: "Borrador", partially_received: "Recepción parcial", received: "Recibida", closed: "Cerrada", cancelled: "Cancelada" };
  const SUPPLIERS: readonly { code: string; name: string }[] = [
    { code: "PROV-TECH", name: "TechImport Ltda." },
    { code: "PROV-ANDES", name: "Distribuidora Andes" },
  ];
  type O = { id: string; number: string; supplierCode: string; total: number; status: string; sku?: string; qty?: number; at: number };
  const money = (v: number): string => `$${Math.round(v).toLocaleString("es-CL")}`;
  const withTax = (net: number): number => net + Math.floor(net * 0.19);
  const daysAgo = (n: number): number => Date.now() - n * 24 * 60 * 60 * 1000;
  let seq = 0;
  const items: O[] = [
    { id: "po1", number: "OC-0001", supplierCode: "PROV-TECH", total: withTax(22000 * 50), status: "approved", at: daysAgo(4) },
    { id: "po2", number: "OC-0002", supplierCode: "PROV-TECH", total: withTax(7500 * 100), status: "approved", at: daysAgo(15) },
    { id: "po3", number: "OC-0003", supplierCode: "PROV-ANDES", total: withTax(12000 * 40), status: "draft", at: daysAgo(22) },
    { id: "po4", number: "OC-0004", supplierCode: "PROV-ANDES", total: withTax(9000 * 30), status: "cancelled", at: daysAgo(41) },
  ];
  type PRange = { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number };
  return {
    overview(range?: PRange): EngineOverviewDto {
      const inWin = (a: O[], f: number, t: number): O[] => a.filter((o) => o.at >= f && o.at <= t);
      const cur = range ? inWin(items, range.fromEpoch, range.toEpoch) : items;
      const prev = range ? inWin(items, range.prevFromEpoch, range.prevToEpoch) : [];
      const appr = (a: O[]): O[] => a.filter((o) => o.status === "approved");
      const spend = (a: O[]): number => appr(a).reduce((s, o) => s + o.total, 0);
      const canc = (a: O[]): number => a.filter((o) => o.status === "cancelled").length;
      const delta = (c: number, p: number): number | undefined => (range && p > 0 ? Math.round(((c - p) / p) * 1000) / 10 : undefined);
      const dset = (c: number, p: number): { deltaPct?: number } => { const d = delta(c, p); return d === undefined ? {} : { deltaPct: d }; };
      return {
        engineId: "purchase", title: "Compras",
        metrics: [
          { label: "Órdenes de compra", value: String(cur.length), accent: "yellow", icon: "purchase", ...dset(cur.length, prev.length) },
          { label: "Aprobadas", value: String(appr(cur).length), accent: "green", icon: "check", ...dset(appr(cur).length, appr(prev).length) },
          { label: "Gasto comprometido", value: money(spend(cur)), accent: "blue", icon: "finance", ...dset(spend(cur), spend(prev)) },
          { label: "Canceladas", value: String(canc(cur)), accent: "red", icon: "alert", ...dset(canc(cur), canc(prev)) },
        ],
        columns: ["Orden", "Proveedor", "Estado", "Total"],
        rows: cur.map((o) => ({ id: o.id, primary: `OC ${o.number}`, secondary: o.supplierCode, amount: money(o.total), status: { label: LABEL[o.status] ?? o.status, tone: TONE[o.status] ?? "neutral" }, at: o.at })),
        ...(range ? { period: { fromEpoch: range.fromEpoch, toEpoch: range.toEpoch } } : {}),
      };
    },
    suppliers() { return { items: SUPPLIERS.map((s) => ({ code: s.code, name: s.name })) }; },
    create(input: { supplierCode?: string; sku?: string; unitCostMinor?: number; quantity?: number }) {
      if (!input.supplierCode?.trim()) return { ok: false, message: "Debes elegir un proveedor" };
      if (!input.sku?.trim()) return { ok: false, message: "Debes elegir un producto" };
      if (!(Number(input.unitCostMinor) > 0)) return { ok: false, message: "El costo debe ser mayor que 0" };
      if (!(Number(input.quantity) > 0)) return { ok: false, message: "La cantidad debe ser mayor que 0" };
      const id = `po-new-${++seq}`;
      const qty = Math.round(Number(input.quantity));
      const net = Math.round(Number(input.unitCostMinor)) * qty;
      items.unshift({ id, number: `OC-${String(1000 + seq).padStart(4, "0")}`, supplierCode: input.supplierCode.trim(), total: withTax(net), status: "draft", sku: input.sku.trim(), qty, at: Date.now() });
      return { ok: true, id };
    },
    pendingLines(id: string): { sku: string; quantity: number }[] {
      const o = items.find((x) => x.id === id);
      return o && o.sku && o.qty && o.status !== "received" ? [{ sku: o.sku, quantity: o.qty }] : [];
    },
    orderFinance(id: string): { party: string; amount: number } | null {
      const o = items.find((x) => x.id === id);
      return o ? { party: o.supplierCode, amount: o.total } : null;
    },
    setStatus(id: string, action: "approve" | "cancel" | "receive") {
      const o = items.find((x) => x.id === id);
      if (!o) return { ok: false, message: "Orden no encontrada" };
      if (action === "approve") {
        if (o.status !== "draft") return { ok: false, message: "Sólo se aprueban órdenes en borrador" };
        o.status = "approved";
      } else if (action === "receive") {
        if (o.status !== "approved" && o.status !== "partially_received") return { ok: false, message: "Sólo se recibe una OC aprobada" };
        o.status = "received";
      } else {
        if (o.status === "received" || o.status === "closed") return { ok: false, message: "No se cancela una orden recibida/cerrada" };
        o.status = "cancelled";
      }
      return { ok: true, id };
    },
  };
}

/** Mock con estado del motor Pricing para dev/navegador (listas de precio que persisten en sesión). */
function createMockPricingStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { published: "success", draft: "warning", archived: "neutral" };
  const LABEL: Record<string, string> = { published: "Publicada", draft: "Borrador", archived: "Archivada" };
  const clp = (v: number): string => `$${Math.round(v).toLocaleString("es-CL")}`;
  type L = { id: string; name: string; currency: string; status: string; items: { ref: string; price: number }[] };
  let seq = 0;
  const items: L[] = [
    { id: "pl1", name: "Lista general Mercado Libre", currency: "CLP", status: "published", items: [{ ref: "ARO-LED-26", price: 45000 }, { ref: "ORG-ACR-01", price: 18000 }, { ref: "TERM-500", price: 27000 }, { ref: "FUN-GEN-01", price: 7000 }] },
    { id: "pl2", name: "Lista mayorista", currency: "CLP", status: "draft", items: [{ ref: "ARO-LED-26", price: 39000 }, { ref: "CARG-3EN1", price: 12500 }] },
  ];
  return {
    overview(): EngineOverviewDto {
      const pricedSkus = items.reduce((a, l) => a + l.items.length, 0);
      return {
        engineId: "pricing", title: "Precios",
        metrics: [
          { label: "Listas de precio", value: String(items.length), accent: "yellow", icon: "pricing" },
          { label: "Publicadas", value: String(items.filter((l) => l.status === "published").length), accent: "green", icon: "check" },
          { label: "En borrador", value: String(items.filter((l) => l.status === "draft").length), accent: "blue", icon: "spark" },
          { label: "Precios definidos", value: String(pricedSkus), accent: "red", icon: "finance" },
        ],
        columns: ["Lista", "Detalle", "Estado", "SKU"],
        rows: items.map((l) => {
          const avg = l.items.length === 0 ? 0 : Math.round(l.items.reduce((a, i) => a + i.price, 0) / l.items.length);
          return {
            id: l.id, primary: l.name,
            secondary: `${l.items.length} precios${l.items.length > 0 ? ` · prom. ${clp(avg)}` : ""} · ${l.currency}`,
            amount: `${l.items.length} SKU`, status: { label: LABEL[l.status] ?? l.status, tone: TONE[l.status] ?? "neutral" },
          };
        }),
      };
    },
    create(input: { name?: string; currency?: string }) {
      if (!input.name?.trim()) return { ok: false, message: "El nombre de la lista es obligatorio" };
      const id = `pl-new-${++seq}`;
      items.unshift({ id, name: input.name.trim(), currency: input.currency?.trim() || "CLP", status: "draft", items: [] });
      return { ok: true, id };
    },
    addPrice(input: { id?: string; productRef?: string; unitPriceMinor?: number }) {
      const l = items.find((x) => x.id === input.id);
      if (!l) return { ok: false, message: "Lista no encontrada" };
      if (l.status !== "draft") return { ok: false, message: "Sólo se editan listas en borrador" };
      if (!input.productRef?.trim()) return { ok: false, message: "El producto es obligatorio" };
      if (!(Number(input.unitPriceMinor) > 0)) return { ok: false, message: "El precio debe ser mayor que 0" };
      const ref = input.productRef.trim();
      const existing = l.items.find((i) => i.ref === ref);
      if (existing) existing.price = Math.round(Number(input.unitPriceMinor));
      else l.items.push({ ref, price: Math.round(Number(input.unitPriceMinor)) });
      return { ok: true, id: l.id };
    },
    setStatus(id: string, action: "publish" | "archive") {
      const l = items.find((x) => x.id === id);
      if (!l) return { ok: false, message: "Lista no encontrada" };
      if (action === "publish") {
        if (l.status !== "draft") return { ok: false, message: "Sólo se publican listas en borrador" };
        if (l.items.length === 0) return { ok: false, message: "La lista no tiene precios" };
        l.status = "published";
      } else {
        if (l.status === "archived") return { ok: false, message: "La lista ya está archivada" };
        l.status = "archived";
      }
      return { ok: true, id };
    },
    priceForSku(sku: string): number {
      const ordered = [...items.filter((l) => l.status === "published"), ...items];
      for (const l of ordered) { const it = l.items.find((i) => i.ref === sku); if (it) return it.price; }
      return 0;
    },
  };
}

/** Mock con estado del motor Logistics para dev/navegador (despachos que persisten en sesión). */
function createMockLogisticsStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { delivered: "success", dispatched: "info", in_transit: "info", ready: "brand", draft: "warning", returned: "warning", cancelled: "danger", closed: "neutral" };
  const LABEL: Record<string, string> = { delivered: "Entregado", dispatched: "Despachado", in_transit: "En tránsito", ready: "Listo", draft: "Borrador", returned: "Devuelto", cancelled: "Cancelado", closed: "Cerrado" };
  type S = { id: string; tracking: string; city: string; weight: number; status: string; at: number };
  const daysAgo = (n: number): number => Date.now() - n * 24 * 60 * 60 * 1000;
  let seq = 0;
  const items: S[] = [
    { id: "sh1", tracking: "TRK-0001", city: "Antofagasta", weight: 2400, status: "delivered", at: daysAgo(3) },
    { id: "sh2", tracking: "TRK-0002", city: "Concepción", weight: 1800, status: "dispatched", at: daysAgo(6) },
    { id: "sh3", tracking: "TRK-0003", city: "Valparaíso", weight: 3200, status: "ready", at: daysAgo(18) },
    { id: "sh4", tracking: "TRK-0005", city: "Temuco", weight: 1500, status: "cancelled", at: daysAgo(44) },
  ];
  type PRange = { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number };
  return {
    overview(range?: PRange): EngineOverviewDto {
      const inWin = (a: S[], f: number, t: number): S[] => a.filter((s) => s.at >= f && s.at <= t);
      const cur = range ? inWin(items, range.fromEpoch, range.toEpoch) : items;
      const prev = range ? inWin(items, range.prevFromEpoch, range.prevToEpoch) : [];
      const enCamino = (a: S[]): number => a.filter((s) => s.status === "dispatched" || s.status === "in_transit").length;
      const listos = (a: S[]): number => a.filter((s) => s.status === "ready").length;
      const entregados = (a: S[]): number => a.filter((s) => s.status === "delivered").length;
      const delta = (c: number, p: number): number | undefined => (range && p > 0 ? Math.round(((c - p) / p) * 1000) / 10 : undefined);
      const dset = (c: number, p: number): { deltaPct?: number } => { const d = delta(c, p); return d === undefined ? {} : { deltaPct: d }; };
      return {
        engineId: "logistics", title: "Logística",
        metrics: [
          { label: "Despachos", value: String(cur.length), accent: "yellow", icon: "logistics", ...dset(cur.length, prev.length) },
          { label: "En camino", value: String(enCamino(cur)), accent: "blue", icon: "pulse", ...dset(enCamino(cur), enCamino(prev)) },
          { label: "Listos", value: String(listos(cur)), accent: "green", icon: "check", ...dset(listos(cur), listos(prev)) },
          { label: "Entregados", value: String(entregados(cur)), accent: "red", icon: "target", ...dset(entregados(cur), entregados(prev)) },
        ],
        columns: ["Despacho", "Destino", "Estado", "Peso"],
        rows: cur.map((s) => ({
          id: s.id, primary: `Despacho ${s.tracking}`, secondary: `Av. Principal 456, ${s.city}, CL`,
          amount: `${(s.weight / 1000).toFixed(1)} kg`, status: { label: LABEL[s.status] ?? s.status, tone: TONE[s.status] ?? "neutral" }, at: s.at,
        })),
        ...(range ? { period: { fromEpoch: range.fromEpoch, toEpoch: range.toEpoch } } : {}),
      };
    },
    create(input: { destinationCity?: string; weightGrams?: number }) {
      if (!input.destinationCity?.trim()) return { ok: false, message: "La ciudad de destino es obligatoria" };
      if (!(Number(input.weightGrams) > 0)) return { ok: false, message: "El peso debe ser mayor que 0" };
      const id = `sh-new-${++seq}`;
      items.unshift({ id, tracking: `TRK-${String(1000 + seq).padStart(4, "0")}`, city: input.destinationCity.trim(), weight: Math.round(Number(input.weightGrams)), status: "ready", at: Date.now() });
      return { ok: true, id };
    },
    setStatus(id: string, action: "dispatch" | "deliver" | "cancel") {
      const s = items.find((x) => x.id === id);
      if (!s) return { ok: false, message: "Despacho no encontrado" };
      if (action === "dispatch") {
        if (s.status !== "ready") return { ok: false, message: "Sólo se despacha un envío Listo" };
        s.status = "dispatched";
      } else if (action === "deliver") {
        if (s.status !== "dispatched" && s.status !== "in_transit") return { ok: false, message: "Sólo se entrega un despacho en tránsito" };
        s.status = "delivered";
      } else {
        if (s.status === "delivered" || s.status === "cancelled") return { ok: false, message: "El despacho ya está en estado final" };
        s.status = "cancelled";
      }
      return { ok: true, id };
    },
  };
}

/** Mock reflectivo del motor Finance para dev/navegador: ingresos (ventas) y egresos (compras). */
function createMockFinanceStore() {
  const clp = (v: number): string => `$${Math.round(v).toLocaleString("es-CL")}`;
  const withTax = (net: number): number => net + Math.floor(net * 0.19);
  type Inv = { id: string; number: string; party: string; net: number; status: string; at: number };
  type Pay = { id: string; number: string; party: string; amount: number; at: number };
  const daysAgo = (n: number): number => Date.now() - n * 24 * 60 * 60 * 1000;
  let seq = 0;
  const invoices: Inv[] = [
    { id: "inv1", number: "FIN-1001", party: "Cliente mayorista Ltda.", net: 2000000, status: "paid", at: daysAgo(3) },
    { id: "inv2", number: "FIN-1002", party: "Distribuidora Andes", net: 1000000, status: "issued", at: daysAgo(12) },
    { id: "inv3", number: "FIN-1003", party: "Retail Sur SpA", net: 722689, status: "paid", at: daysAgo(40) },
  ];
  const payments: Pay[] = [
    { id: "pay1", number: "PAY-2001", party: "Importadora Pacífico", amount: withTax(650000), at: daysAgo(8) },
    { id: "pay2", number: "PAY-2002", party: "Insumos del Sur", amount: withTax(320000), at: daysAgo(38) },
  ];
  const LABEL: Record<string, string> = { paid: "Pagada", issued: "Emitida", draft: "Borrador" };
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { paid: "success", issued: "info", draft: "warning" };
  type FinRange = { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number };
  return {
    overview(range?: FinRange): EngineOverviewDto {
      const inWin = <T extends { at: number }>(a: readonly T[], f: number, t: number): T[] => a.filter((x) => x.at >= f && x.at <= t);
      const curInv = range ? inWin(invoices, range.fromEpoch, range.toEpoch) : invoices;
      const curPay = range ? inWin(payments, range.fromEpoch, range.toEpoch) : payments;
      const prevInv = range ? inWin(invoices, range.prevFromEpoch, range.prevToEpoch) : [];
      const prevPay = range ? inWin(payments, range.prevFromEpoch, range.prevToEpoch) : [];
      const ing = (a: Inv[]): number => a.reduce((s, i) => s + withTax(i.net), 0);
      const ivaOf = (a: Inv[]): number => a.reduce((s, i) => s + Math.floor(i.net * 0.19), 0);
      const egr = (a: Pay[]): number => a.reduce((s, p) => s + p.amount, 0);
      const delta = (c: number, p: number): number | undefined => (range && p > 0 ? Math.round(((c - p) / p) * 1000) / 10 : undefined);
      const dset = (c: number, p: number): { deltaPct?: number } => { const d = delta(c, p); return d === undefined ? {} : { deltaPct: d }; };
      const ingC = ing(curInv), egrC = egr(curPay);
      const invoiceRows = curInv.map((i) => ({ id: i.id, primary: `Factura ${i.number}`, secondary: `Venta · ${i.party}`, amount: clp(withTax(i.net)), status: { label: LABEL[i.status] ?? i.status, tone: TONE[i.status] ?? "neutral" }, at: i.at }));
      const paymentRows = curPay.map((p) => ({ id: p.id, primary: `Pago ${p.number}`, secondary: `Compra · ${p.party}`, amount: `-${clp(p.amount)}`, status: { label: "Egreso", tone: "danger" as const }, at: p.at }));
      return {
        engineId: "finance", title: "Finanzas",
        metrics: [
          { label: "Ingresos (ventas)", value: clp(ingC), accent: "green", icon: "arrow-up", ...dset(ingC, ing(prevInv)) },
          { label: "Egresos (compras)", value: clp(egrC), accent: "red", icon: "arrow-down", ...dset(egrC, egr(prevPay)) },
          { label: "IVA débito", value: clp(ivaOf(curInv)), accent: "yellow", icon: "finance", ...dset(ivaOf(curInv), ivaOf(prevInv)) },
          { label: "Flujo neto", value: clp(ingC - egrC), accent: "blue", icon: "pulse", ...dset(ingC - egrC, ing(prevInv) - egr(prevPay)) },
        ],
        columns: ["Documento", "Movimiento", "Estado", "Monto"],
        rows: [...paymentRows, ...invoiceRows].sort((a, b) => b.at - a.at),
        ...(range ? { period: { fromEpoch: range.fromEpoch, toEpoch: range.toEpoch } } : {}),
      };
    },
    recordSale(party: string, net: number) {
      if (!(net > 0)) return;
      invoices.unshift({ id: `inv-new-${++seq}`, number: `FIN-${String(1100 + seq)}`, party: party || "Cliente", net: Math.round(net), status: "issued", at: Date.now() });
    },
    recordPurchase(party: string, amount: number) {
      if (!(amount > 0)) return;
      payments.unshift({ id: `pay-new-${++seq}`, number: `PAY-${String(2100 + seq)}`, party: party || "Proveedor", amount: Math.round(amount), at: Date.now() });
    },
  };
}

/** Mock con estado del motor AI para dev/navegador (registro de modelos que persiste en sesión). */
function createMockAiStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { active: "success", draft: "warning", deprecated: "info", retired: "neutral" };
  const LABEL: Record<string, string> = { active: "Activo", draft: "Borrador", deprecated: "Obsoleto", retired: "Retirado" };
  type M = { id: string; name: string; provider: string; ctx: number; status: string };
  let seq = 0;
  const items: M[] = [
    { id: "ai1", name: "Claude Opus", provider: "anthropic", ctx: 200000, status: "active" },
    { id: "ai2", name: "GPT-4o", provider: "openai", ctx: 128000, status: "active" },
    { id: "ai3", name: "text-embedding-3", provider: "openai", ctx: 8191, status: "active" },
    { id: "ai4", name: "Llama 3 (local)", provider: "ollama", ctx: 8192, status: "draft" },
    { id: "ai5", name: "GPT-3.5 (legacy)", provider: "openai", ctx: 16385, status: "retired" },
  ];
  const providers = (): number => new Set(items.map((m) => m.provider)).size;
  return {
    overview(): EngineOverviewDto {
      return {
        engineId: "ai", title: "IA",
        metrics: [
          { label: "Modelos", value: String(items.length), accent: "yellow", icon: "ai" },
          { label: "Activos", value: String(items.filter((m) => m.status === "active").length), accent: "green", icon: "check" },
          { label: "Proveedores", value: String(providers()), accent: "blue", icon: "spark" },
          { label: "Retirados", value: String(items.filter((m) => m.status === "retired").length), accent: "red", icon: "shield" },
        ],
        columns: ["Modelo", "Proveedor", "Estado", ""],
        rows: items.map((m) => ({ id: m.id, primary: m.name, secondary: `${m.provider} · ${(m.ctx / 1000).toFixed(0)}k contexto`, amount: "—", status: { label: LABEL[m.status] ?? m.status, tone: TONE[m.status] ?? "neutral" } })),
      };
    },
    register(input: { name?: string; provider?: string; contextWindow?: number }) {
      if (!input.name?.trim()) return { ok: false, message: "El nombre del modelo es obligatorio" };
      const provider = (input.provider ?? "").trim().toLowerCase();
      if (!["anthropic", "openai", "ollama"].includes(provider)) return { ok: false, message: "Proveedor no soportado (anthropic, openai u ollama)" };
      if (items.some((m) => m.name === input.name!.trim())) return { ok: false, message: `Ya existe un modelo con nombre ${input.name}` };
      const id = `ai-new-${++seq}`;
      items.unshift({ id, name: input.name.trim(), provider, ctx: input.contextWindow && input.contextWindow > 0 ? Math.round(input.contextWindow) : 8192, status: "draft" });
      return { ok: true, id };
    },
    setStatus(id: string, action: "activate" | "retire") {
      const m = items.find((x) => x.id === id);
      if (!m) return { ok: false, message: "Modelo no encontrado" };
      m.status = action === "activate" ? "active" : "retired";
      return { ok: true, id };
    },
  };
}

/** Mock con estado del motor Marketplace para dev/navegador (publicaciones que persisten en sesión). */
function createMockMarketplaceStore() {
  const TONE: Record<string, EngineRowDtoLite["status"]["tone"]> = { published: "success", paused: "info", draft: "warning", closed: "neutral", deleted: "neutral" };
  const LABEL: Record<string, string> = { published: "Publicado", paused: "Pausado", draft: "Borrador", closed: "Cerrado", deleted: "Eliminado" };
  type P = { id: string; sku: string; title: string; price: number; status: string };
  let seq = 0;
  const items: P[] = [
    { id: "mp1", sku: "ARO-LED-26", title: "Aro de luz LED 26cm con trípode", price: 54990, status: "published" },
    { id: "mp2", sku: "ORG-ACR-01", title: "Organizador de maquillaje acrílico", price: 21500, status: "published" },
    { id: "mp3", sku: "TERM-500", title: "Botella térmica inteligente 500ml", price: 32990, status: "paused" },
    { id: "mp4", sku: "CARG-3EN1", title: "Cargador inalámbrico 3 en 1", price: 18990, status: "published" },
    { id: "mp5", sku: "FUN-GEN-01", title: "Funda genérica para celular", price: 8990, status: "draft" },
  ];
  const clp = (v: number): string => `$${Math.round(v).toLocaleString("es-CL")}`;
  const visible = (): P[] => items.filter((p) => p.status !== "deleted");
  return {
    overview(): EngineOverviewDto {
      const pubs = visible();
      const count = (s: string): number => pubs.filter((p) => p.status === s).length;
      return {
        engineId: "marketplace", title: "Marketplace",
        metrics: [
          { label: "Publicaciones", value: String(pubs.length), accent: "yellow", icon: "marketplace" },
          { label: "Publicadas", value: String(count("published")), accent: "green", icon: "check" },
          { label: "Pausadas", value: String(count("paused")), accent: "blue", icon: "pulse" },
          { label: "Borradores", value: String(count("draft")), accent: "red", icon: "spark" },
        ],
        columns: ["Publicación", "Detalle", "Estado", "Precio"],
        rows: pubs.map((p) => ({ id: p.id, primary: p.title, secondary: `SKU ${p.sku}`, amount: clp(p.price), status: { label: LABEL[p.status] ?? p.status, tone: TONE[p.status] ?? "neutral" } })),
      };
    },
    create(input: { sku?: string; title?: string; priceMinorUnits?: number; stock?: number }) {
      if (!input.sku?.trim()) return { ok: false, message: "El SKU es obligatorio" };
      if (!input.title?.trim()) return { ok: false, message: "El título es obligatorio" };
      if (!(Number(input.priceMinorUnits) > 0)) return { ok: false, message: "El precio debe ser mayor que 0" };
      const id = `mp-new-${++seq}`;
      items.unshift({ id, sku: input.sku.trim(), title: input.title.trim(), price: Math.round(Number(input.priceMinorUnits)), status: "draft" });
      return { ok: true, id };
    },
    skuOf(id: string): string | null { return items.find((x) => x.id === id)?.sku ?? null; },
    setStatus(id: string, action: "publish" | "pause" | "resume" | "delete") {
      const p = items.find((x) => x.id === id);
      if (!p) return { ok: false, message: "Publicación no encontrada" };
      if (action === "publish") {
        if (p.status !== "draft") return { ok: false, message: "Sólo se publica un borrador" };
        p.status = "published";
      } else if (action === "pause") {
        if (p.status !== "published") return { ok: false, message: "Sólo se pausa una publicación activa" };
        p.status = "paused";
      } else if (action === "resume") {
        if (p.status !== "paused") return { ok: false, message: "Sólo se reanuda una pausada" };
        p.status = "published";
      } else {
        p.status = "deleted";
      }
      return { ok: true, id };
    },
  };
}

/** Panel Analytics EN VIVO para dev/navegador: deriva sus KPIs de los otros mock stores. */
function createMockAnalyticsStore(sources: { overview(): EngineOverviewDto }[]) {
  const metricValue = (ov: EngineOverviewDto, label: string): string | undefined =>
    ov.metrics.find((m) => m.label.toLowerCase().includes(label.toLowerCase()))?.value;
  const num = (v: string | undefined): number => { const d = (v ?? "").replace(/[^\d]/g, ""); return d.length > 0 ? Number(d) : 0; };
  const statusOf = (att: number): EngineOverviewDto["rows"][number]["status"] =>
    att >= 100 ? { label: "En meta", tone: "success" } : att >= 80 ? { label: "En riesgo", tone: "warning" } : { label: "Fuera de meta", tone: "danger" };
  return {
    overview(): EngineOverviewDto {
      const by = (engineId: string): EngineOverviewDto | undefined => sources.map((s) => s.overview()).find((o) => o.engineId === engineId);
      const sales = by("sales"); const inventory = by("inventory"); const logistics = by("logistics");
      const purchase = by("purchase"); const pricing = by("pricing");
      const defs = [
        { name: "Ingreso confirmado (K$)", current: Math.round(num(sales && metricValue(sales, "ingreso")) / 1000), target: 300 },
        { name: "Órdenes de venta confirmadas", current: num(sales && metricValue(sales, "confirmadas")), target: 5 },
        { name: "Unidades en stock", current: num(inventory && metricValue(inventory, "unidades")), target: 200 },
        { name: "Despachos entregados", current: num(logistics && metricValue(logistics, "entregados")), target: 2 },
        { name: "Compras aprobadas", current: num(purchase && metricValue(purchase, "aprobadas")), target: 3 },
        { name: "Listas de precio publicadas", current: num(pricing && metricValue(pricing, "publicadas")), target: 2 },
      ];
      const kpis = defs.map((d) => ({ ...d, attainment: Math.round((d.current / d.target) * 100) }));
      const avg = kpis.length === 0 ? 0 : Math.round(kpis.reduce((a, k) => a + k.attainment, 0) / kpis.length);
      return {
        engineId: "analytics", title: "Analytics",
        metrics: [
          { label: "KPIs monitoreados", value: String(kpis.length), accent: "yellow", icon: "analytics" },
          { label: "En meta", value: String(kpis.filter((k) => k.attainment >= 100).length), accent: "green", icon: "check" },
          { label: "En riesgo", value: String(kpis.filter((k) => k.attainment >= 80 && k.attainment < 100).length), accent: "blue", icon: "pulse" },
          { label: "Cumplimiento prom.", value: `${avg}%`, accent: "red", icon: "target" },
        ],
        columns: ["KPI", "Detalle", "Estado", "Cumplim."],
        rows: kpis.map((k, i) => ({ id: `kpi-${i}`, primary: k.name, secondary: `Actual ${k.current} · Meta ${k.target}`, amount: `${k.attainment}%`, status: statusOf(k.attainment) })),
      };
    },
  };
}

export const PRODUCT_DEVELOPMENT_OVERVIEW: ProductDevelopmentOverviewDto = {
  kpis: [
    { key: "investigations", label: "Investigaciones", value: "24", deltaPct: 20.0, accent: "yellow", icon: "spark" },
    { key: "winners", label: "Ganadores", value: "6", deltaPct: 15.0, accent: "green", icon: "target" },
    { key: "winrate", label: "Tasa de éxito", value: "25%", deltaPct: 4.0, accent: "blue", icon: "pulse" },
    { key: "opportunity", label: "Oportunidad estimada", value: "$12.4M", deltaPct: 9.5, accent: "yellow", icon: "bolt" },
  ],
  candidates: [
    {
      id: "cand-1", name: "Aro de luz LED 26cm con trípode", category: "Tecnología / Foto y Video / Iluminación",
      verdict: "exceptional", successProbability: 93, monthlyUnits: 420, marginPct: 44,
      indices: indices([95, 78, 88, 82, 74, 86, 80, 84, 90, 66, 58, 93, 96]),
    },
    {
      id: "cand-2", name: "Organizador de maquillaje acrílico", category: "Belleza / Organización",
      verdict: "star", successProbability: 85, monthlyUnits: 300, marginPct: 51,
      indices: indices([82, 70, 84, 88, 80, 78, 82, 72, 76, 60, 48, 85, 80]),
    },
    {
      id: "cand-3", name: "Botella térmica inteligente 500ml", category: "Hogar / Cocina / Termos",
      verdict: "recommended", successProbability: 74, monthlyUnits: 260, marginPct: 39,
      indices: indices([76, 55, 72, 70, 68, 74, 66, 70, 72, 72, 64, 74, 78]),
    },
    {
      id: "cand-4", name: "Cargador inalámbrico 3 en 1", category: "Tecnología / Accesorios / Carga",
      verdict: "medium_risk", successProbability: 55, monthlyUnits: 180, marginPct: 28,
      indices: indices([70, 30, 52, 48, 60, 58, 62, 55, 50, 40, 42, 55, 60]),
    },
    {
      id: "cand-5", name: "Funda genérica para celular", category: "Tecnología / Accesorios / Fundas",
      verdict: "no_sell", successProbability: 22, monthlyUnits: 90, marginPct: 12,
      indices: indices([40, 8, 30, 18, 55, 35, 60, 25, 20, 10, 8, 22, 30]),
    },
  ],
};

/** Construye un transporte simulado con los canales de lectura cableados. */
export function buildMockTransport(options: { latencyMs?: number } = {}): Transport {
  const transport = new MockTransport(options);
  transport.on(IPC_CHANNELS.brainOverview, (payload) => {
    const range = (payload as { range?: { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number } } | undefined)?.range;
    if (!range) return EXECUTIVE_OVERVIEW;
    // KPIs de período (ventas/órdenes) desde Ventas filtrado; el resto queda como estado actual.
    const sales = mockSales.overview(range);
    const mv = (l: string) => sales.metrics.find((m) => m.label.toLowerCase().includes(l));
    const rev = mv("ingreso"); const ord = mv("confirmadas");
    const kpis = EXECUTIVE_OVERVIEW.kpis.map((k) =>
      k.key === "revenue" ? { ...k, value: rev?.value ?? k.value, deltaPct: rev?.deltaPct ?? 0 }
      : k.key === "orders" ? { ...k, value: ord?.value ?? k.value, deltaPct: ord?.deltaPct ?? 0 }
      : k,
    );
    return { ...EXECUTIVE_OVERVIEW, kpis, period: { fromEpoch: range.fromEpoch, toEpoch: range.toEpoch } };
  });
  const mockProduct = createMockProductStore();
  // (handler de recomendación IA se registra más abajo, cuando los stores ya existen)
  const mockCustomer = createMockCustomerStore();
  const mockSupplier = createMockSupplierStore();
  const mockSales = createMockSalesStore();
  const mockInventory = createMockInventoryStore();
  const mockPurchase = createMockPurchaseStore();
  const mockPricing = createMockPricingStore();
  const mockLogistics = createMockLogisticsStore();
  const mockMarketplace = createMockMarketplaceStore();
  const mockAi = createMockAiStore();
  const mockFinance = createMockFinanceStore();
  const mockAnalytics = createMockAnalyticsStore([mockSales, mockInventory, mockLogistics, mockPurchase, mockPricing]);
  // Multi-cuenta (demostración en memoria): una cuenta "Personal" por defecto.
  const accts: { id: string; nickname: string; createdAt: number; connected: boolean }[] = [{ id: "acc_personal", nickname: "Personal", createdAt: Date.now(), connected: false }];
  let activeAcct = "acc_personal";
  const acctSnap = () => ({ items: accts.map((a) => ({ ...a, connected: a.id === "acc_personal" ? creds.status().mercadolibre.configured : a.connected })), activeId: activeAcct });
  transport.on(IPC_CHANNELS.accountsList, () => acctSnap());
  transport.on(IPC_CHANNELS.accountsAdd, (payload) => {
    const nick = ((payload as { nickname?: string } | undefined)?.nickname || "Cuenta").trim();
    const id = `acc_${Date.now().toString(36)}`;
    accts.push({ id, nickname: nick, createdAt: Date.now(), connected: false }); activeAcct = id;
    return acctSnap();
  });
  transport.on(IPC_CHANNELS.accountsSetActive, (payload) => {
    const id = (payload as { id?: string } | undefined)?.id ?? "";
    if (accts.some((a) => a.id === id)) activeAcct = id;
    return acctSnap();
  });
  transport.on(IPC_CHANNELS.accountsRename, (payload) => {
    const p = payload as { id?: string; nickname?: string } | undefined;
    const a = accts.find((x) => x.id === p?.id); if (a && (p?.nickname || "").trim()) a.nickname = p!.nickname!.trim();
    return acctSnap();
  });
  transport.on(IPC_CHANNELS.accountsRemove, (payload) => {
    const id = (payload as { id?: string } | undefined)?.id ?? "";
    if (accts.length > 1) { const i = accts.findIndex((a) => a.id === id); if (i >= 0) { accts.splice(i, 1); if (activeAcct === id) activeAcct = accts[0]!.id; } }
    return acctSnap();
  });
  transport.on(IPC_CHANNELS.accountsConsolidated, () => {
    const rows = acctSnap().items.map((a, i) => ({ id: a.id, nickname: a.nickname, connected: a.connected, ingreso: a.connected ? 48 - i * 10 : 0, ordenes: a.connected ? 4 : 0, stock: a.connected ? 180 : 0 }));
    const totals = rows.reduce((t, r) => ({ ingreso: t.ingreso + r.ingreso, ordenes: t.ordenes + r.ordenes, stock: t.stock + r.stock }), { ingreso: 0, ordenes: 0, stock: 0 });
    return { accounts: rows, totals };
  });

  // Analytics: KPIs con metas configurables (demostración en memoria).
  const kpiSpecs = [
    { key: "ingreso", name: "Ingreso confirmado (K$)", current: 48, def: 300 },
    { key: "ordenes", name: "Órdenes de venta confirmadas", current: 4, def: 5 },
    { key: "stock", name: "Unidades en stock", current: 180, def: 200 },
    { key: "despachos", name: "Despachos entregados", current: 2, def: 2 },
    { key: "compras", name: "Compras aprobadas", current: 3, def: 3 },
    { key: "listas", name: "Listas de precio publicadas", current: 2, def: 2 },
  ];
  const kpiTargets = new Map<string, number>();
  // KPIs de PERÍODO (ingreso, órdenes) se derivan de Ventas filtrado por rango (FF4); el resto es estado actual.
  const numOf = (v: string | undefined): number => { const d = (v ?? "").replace(/[^\d]/g, ""); return d.length > 0 ? Number(d) : 0; };
  const kpiRows = (range?: { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number }) => {
    const sales = mockSales.overview(range);
    const mv = (label: string): string | undefined => sales.metrics.find((m) => m.label.toLowerCase().includes(label))?.value;
    const dyn: Record<string, number> = { ingreso: Math.round(numOf(mv("ingreso")) / 1000), ordenes: numOf(mv("confirmadas")) };
    return { items: kpiSpecs.map((s) => {
      const current = dyn[s.key] ?? s.current;
      const target = kpiTargets.get(s.key) ?? s.def;
      const attainment = target > 0 ? Math.round((current / target) * 100) : 0;
      const status = attainment >= 100 ? "on_track" : attainment >= 80 ? "at_risk" : "off_track";
      const statusLabel = status === "on_track" ? "En meta" : status === "at_risk" ? "En riesgo" : "Fuera de meta";
      return { key: s.key, name: s.name, current, target, attainment, status, statusLabel, isCustom: kpiTargets.has(s.key) };
    }) };
  };
  transport.on(IPC_CHANNELS.analyticsTargetsList, (payload) => kpiRows((payload as { range?: { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number } } | undefined)?.range));
  transport.on(IPC_CHANNELS.analyticsTargetsSet, (payload) => {
    const p = payload as { key?: string; target?: number } | undefined;
    if (p?.key && Number.isFinite(p.target) && (p.target ?? 0) > 0) kpiTargets.set(p.key, Number(p.target));
    return kpiRows();
  });

  transport.on(IPC_CHANNELS.engineOverview, (payload) => {
    const p = payload as { engineId?: string; range?: { fromEpoch: number; toEpoch: number; prevFromEpoch: number; prevToEpoch: number } } | undefined;
    const engineId = p?.engineId ?? "product";
    if (engineId === "ai") return mockAi.overview();
    if (engineId === "finance") return mockFinance.overview(p?.range);
    if (engineId === "product") return mockProduct.overview();
    if (engineId === "customer") return mockCustomer.overview();
    if (engineId === "supplier") return mockSupplier.overview();
    if (engineId === "sales") return mockSales.overview(p?.range);
    if (engineId === "inventory") return mockInventory.overview();
    if (engineId === "purchase") return mockPurchase.overview(p?.range);
    if (engineId === "pricing") return mockPricing.overview();
    if (engineId === "logistics") return mockLogistics.overview(p?.range);
    if (engineId === "marketplace") return mockMarketplace.overview();
    if (engineId === "analytics") return mockAnalytics.overview();
    return engineOverviewFor(engineId);
  });
  transport.on(IPC_CHANNELS.productCreate, (payload) => mockProduct.create((payload ?? {}) as { sku?: string; name?: string; unit?: string }));
  transport.on(IPC_CHANNELS.productActivate, (payload) => mockProduct.setStatus((payload as { id: string }).id, "activate"));
  transport.on(IPC_CHANNELS.productDeactivate, (payload) => mockProduct.setStatus((payload as { id: string }).id, "deactivate"));
  transport.on(IPC_CHANNELS.productArchive, (payload) => mockProduct.setStatus((payload as { id: string }).id, "archive"));
  transport.on(IPC_CHANNELS.productCatalog, () => mockProduct.catalog());
  transport.on(IPC_CHANNELS.skuInfo, (payload) => {
    const sku = (payload as { sku?: string } | undefined)?.sku ?? "";
    return { sku, priceMinor: mockPricing.priceForSku(sku), stock: mockInventory.availableForSku(sku) };
  });
  transport.on(IPC_CHANNELS.customerCreate, (payload) => mockCustomer.create((payload ?? {}) as { code?: string; name?: string; type?: string }));
  transport.on(IPC_CHANNELS.customerActivate, (payload) => mockCustomer.setStatus((payload as { id: string }).id, "activate"));
  transport.on(IPC_CHANNELS.customerDeactivate, (payload) => mockCustomer.setStatus((payload as { id: string }).id, "deactivate"));
  transport.on(IPC_CHANNELS.customerArchive, (payload) => mockCustomer.setStatus((payload as { id: string }).id, "archive"));
  transport.on(IPC_CHANNELS.supplierCreate, (payload) => mockSupplier.create((payload ?? {}) as { code?: string; name?: string; type?: string; country?: string }));
  transport.on(IPC_CHANNELS.supplierActivate, (payload) => mockSupplier.setStatus((payload as { id: string }).id, "activate"));
  transport.on(IPC_CHANNELS.supplierDeactivate, (payload) => mockSupplier.setStatus((payload as { id: string }).id, "deactivate"));
  transport.on(IPC_CHANNELS.supplierArchive, (payload) => mockSupplier.setStatus((payload as { id: string }).id, "archive"));
  transport.on(IPC_CHANNELS.supplierBlacklist, (payload) => mockSupplier.setStatus((payload as { id: string }).id, "blacklist"));
  transport.on(IPC_CHANNELS.salesCreate, (payload) => mockSales.create((payload ?? {}) as { customerCode?: string; sku?: string; unitPriceMinor?: number; quantity?: number }));
  transport.on(IPC_CHANNELS.salesConfirm, (payload) => {
    const id = (payload as { id: string }).id;
    const r = mockSales.setStatus(id, "confirm");
    // Coherencia Fase 3: venta confirmada DESCUENTA stock, REFLEJA ingreso y CREA el despacho.
    if (r.ok) {
      const lines = mockSales.orderLines(id);
      for (const line of lines) mockInventory.issueBySku(line.sku, line.quantity);
      const f = mockSales.orderFinance(id);
      if (f) {
        mockFinance.recordSale(f.party, f.net);
        const units = lines.reduce((acc, l) => acc + l.quantity, 0);
        mockLogistics.create({ destinationCity: f.party, weightGrams: Math.max(1, units) * 500 });
      }
    }
    return r;
  });
  transport.on(IPC_CHANNELS.salesCancel, (payload) => mockSales.setStatus((payload as { id: string }).id, "cancel"));
  transport.on(IPC_CHANNELS.salesCustomers, () => mockSales.customers());
  transport.on(IPC_CHANNELS.inventoryCreate, (payload) => mockInventory.create((payload ?? {}) as { sku?: string; initialOnHand?: number }));
  transport.on(IPC_CHANNELS.inventoryMove, (payload) => {
    const p = (payload ?? {}) as { id?: string; action?: "receive" | "issue" | "adjust"; quantity?: number };
    return mockInventory.move(p.id ?? "", p.action ?? "receive", Number(p.quantity ?? 0));
  });
  transport.on(IPC_CHANNELS.purchaseCreate, (payload) => mockPurchase.create((payload ?? {}) as { supplierCode?: string; sku?: string; unitCostMinor?: number; quantity?: number }));
  transport.on(IPC_CHANNELS.purchaseApprove, (payload) => mockPurchase.setStatus((payload as { id: string }).id, "approve"));
  transport.on(IPC_CHANNELS.purchaseCancel, (payload) => mockPurchase.setStatus((payload as { id: string }).id, "cancel"));
  transport.on(IPC_CHANNELS.purchaseReceive, (payload) => {
    const id = (payload as { id: string }).id;
    const pending = mockPurchase.pendingLines(id);
    const finance = mockPurchase.orderFinance(id);
    const r = mockPurchase.setStatus(id, "receive");
    // Coherencia Fase 3: compra recibida SUMA stock y REFLEJA el egreso en Finanzas.
    if (r.ok) {
      for (const line of pending) mockInventory.receiveBySku(line.sku, line.quantity);
      if (finance) mockFinance.recordPurchase(finance.party, finance.amount);
    }
    return r;
  });
  transport.on(IPC_CHANNELS.purchaseSuppliers, () => mockPurchase.suppliers());
  transport.on(IPC_CHANNELS.pricingCreate, (payload) => mockPricing.create((payload ?? {}) as { name?: string; currency?: string }));
  transport.on(IPC_CHANNELS.pricingAddPrice, (payload) => mockPricing.addPrice((payload ?? {}) as { id?: string; productRef?: string; unitPriceMinor?: number }));
  transport.on(IPC_CHANNELS.pricingPublish, (payload) => mockPricing.setStatus((payload as { id: string }).id, "publish"));
  transport.on(IPC_CHANNELS.pricingArchive, (payload) => mockPricing.setStatus((payload as { id: string }).id, "archive"));
  transport.on(IPC_CHANNELS.logisticsCreate, (payload) => mockLogistics.create((payload ?? {}) as { destinationCity?: string; weightGrams?: number }));
  transport.on(IPC_CHANNELS.logisticsDispatch, (payload) => mockLogistics.setStatus((payload as { id: string }).id, "dispatch"));
  transport.on(IPC_CHANNELS.logisticsDeliver, (payload) => mockLogistics.setStatus((payload as { id: string }).id, "deliver"));
  transport.on(IPC_CHANNELS.logisticsCancel, (payload) => mockLogistics.setStatus((payload as { id: string }).id, "cancel"));
  transport.on(IPC_CHANNELS.marketplaceCreate, (payload) => mockMarketplace.create((payload ?? {}) as { sku?: string; title?: string; priceMinorUnits?: number; stock?: number }));
  transport.on(IPC_CHANNELS.marketplacePublish, (payload) => mockMarketplace.setStatus((payload as { id: string }).id, "publish"));
  transport.on(IPC_CHANNELS.marketplacePause, (payload) => mockMarketplace.setStatus((payload as { id: string }).id, "pause"));
  transport.on(IPC_CHANNELS.marketplaceResume, (payload) => mockMarketplace.setStatus((payload as { id: string }).id, "resume"));
  transport.on(IPC_CHANNELS.marketplaceDelete, (payload) => mockMarketplace.setStatus((payload as { id: string }).id, "delete"));
  const mlIds = new Map<string, string>();
  transport.on(IPC_CHANNELS.marketplaceSyncMl, (payload) => {
    // Simula Item 1+2: sin credenciales pide configurarlas; con ellas crea el aviso la primera vez
    // y ACTUALIZA precio (Precios) + stock (Inventario) reales las siguientes.
    if (!creds.status().mercadolibre.configured) return { ok: false, message: "Configura las credenciales de Mercado Libre en Seguridad" };
    const id = (payload as { id: string }).id;
    const sku = mockMarketplace.skuOf(id) ?? "";
    const price = mockPricing.priceForSku(sku);
    const stock = mockInventory.availableForSku(sku);
    const clp = (v: number): string => `$${Math.round(v).toLocaleString("es-CL")}`;
    const existing = mlIds.get(id);
    if (existing) return { ok: true, id: existing, message: `Precio (${clp(price)}) y stock (${stock}) sincronizados en Mercado Libre (demostración)` };
    const ext = `MLC${id.replace(/[^a-zA-Z0-9]/g, "").slice(0, 8).toUpperCase()}`;
    mlIds.set(id, ext);
    return { ok: true, id: ext, message: `Publicado en Mercado Libre (demostración, ${ext})` };
  });
  // Sincronización completa simulada (item 6): importa órdenes + sincroniza publicaciones.
  transport.on(IPC_CHANNELS.marketplaceSyncAll, () => {
    if (!creds.status().mercadolibre.configured) return { ok: false, imported: 0, listingsImported: 0, synced: 0, failed: 0, message: "Configura las credenciales de Mercado Libre en Seguridad" };
    return { ok: true, imported: 0, listingsImported: 1, synced: 2, failed: 0, message: "Sincronización: 1 publicación(es) traída(s), 0 orden(es) importada(s), 2 publicación(es) al día. (demostración)" };
  });
  let importedMl = false;
  transport.on(IPC_CHANNELS.marketplaceImportOrders, () => {
    // Simula Item 3: trae órdenes de ML y las crea como ventas confirmadas (efectos Fase 3).
    if (!creds.status().mercadolibre.configured) return { ok: false, message: "Configura las credenciales de Mercado Libre en Seguridad" };
    if (importedMl) return { ok: false, message: "No hay órdenes nuevas en Mercado Libre." };
    const fake = [
      { buyer: "juan_ml", sku: "ARO-LED-26", qty: 2, price: 45000 },
      { buyer: "maria_ml", sku: "ORG-ACR-01", qty: 1, price: 18000 },
    ];
    let n = 0;
    for (const o of fake) {
      const created = mockSales.create({ customerCode: `ML-${o.buyer}`.toUpperCase(), sku: o.sku, unitPriceMinor: o.price, quantity: o.qty });
      if (!created.ok || !created.id) continue;
      if (!mockSales.setStatus(created.id, "confirm").ok) continue;
      for (const line of mockSales.orderLines(created.id)) mockInventory.issueBySku(line.sku, line.quantity);
      const f = mockSales.orderFinance(created.id);
      if (f) { mockFinance.recordSale(f.party, f.net); mockLogistics.create({ destinationCity: f.party, weightGrams: Math.max(1, o.qty) * 500 }); }
      n++;
    }
    importedMl = true;
    return { ok: n > 0, message: n > 0 ? `${n} orden(es) importada(s) de Mercado Libre y confirmada(s).` : "No se pudo importar ninguna orden." };
  });
  let importedMlListings = false;
  transport.on(IPC_CHANNELS.marketplaceImportListings, () => {
    // Simula traer las publicaciones reales de ML al catálogo local (demostración: 1 publicación).
    if (!creds.status().mercadolibre.configured) return { ok: false, message: "Configura las credenciales de Mercado Libre en Seguridad" };
    if (importedMlListings) return { ok: false, message: "Tus publicaciones ya estaban reflejadas en el catálogo." };
    const created = mockMarketplace.create({ sku: "MLC-DEMO-01", title: "Publicación real (demostración)", priceMinorUnits: 24990, stock: 1 });
    if (created.ok && created.id) mockMarketplace.setStatus(created.id, "publish");
    importedMlListings = true;
    return { ok: true, imported: 1, message: "1 publicación(es) traída(s) de Mercado Libre y reflejada(s) en el catálogo." };
  });
  transport.on(IPC_CHANNELS.aiRegister, (payload) => mockAi.register((payload ?? {}) as { name?: string; provider?: string; contextWindow?: number }));
  transport.on(IPC_CHANNELS.aiActivate, (payload) => mockAi.setStatus((payload as { id: string }).id, "activate"));
  transport.on(IPC_CHANNELS.aiRetire, (payload) => mockAi.setStatus((payload as { id: string }).id, "retire"));
  const pd = new PdBackend(PRODUCT_DEVELOPMENT_OVERVIEW.candidates);
  transport.on(IPC_CHANNELS.productDevelopmentOverview, () => pd.overview());
  transport.on(IPC_CHANNELS.productDevelopmentOpen, (payload) => pd.open(payload as OpenInvestigationInput));
  transport.on(IPC_CHANNELS.productDevelopmentSimulate, (payload) => {
    const result = pd.simulate(payload as SimulateInput);
    if (!result) throw new Error("Candidato no encontrado");
    return result;
  });
  const creds = createMockCredentials();
  transport.on(IPC_CHANNELS.credentialsStatus, () => creds.status());
  transport.on(IPC_CHANNELS.credentialsSave, (payload) => creds.save(payload as SaveCredentialsInput));
  transport.on(IPC_CHANNELS.credentialsTest, (payload) => creds.test((payload as { provider: string }).provider));
  transport.on(IPC_CHANNELS.mlBeginConnect, (payload) => creds.mlBeginConnect(payload as { clientId: string; clientSecret: string; redirectUri: string; authBase?: string }));
  transport.on(IPC_CHANNELS.mlCompleteConnect, (payload) => creds.mlCompleteConnect((payload as { code: string }).code));
  transport.on(IPC_CHANNELS.onboardingStatus, () => creds.onboardingStatus());
  transport.on(IPC_CHANNELS.onboardingComplete, () => creds.completeOnboarding());
  transport.on(IPC_CHANNELS.syncConfigGet, () => creds.syncGet());
  transport.on(IPC_CHANNELS.syncConfigSave, (payload) => creds.syncSave((payload ?? {}) as Partial<{ enabled: boolean; intervalMinutes: number }>));
  transport.on(IPC_CHANNELS.businessConfigGet, () => creds.businessGet());
  transport.on(IPC_CHANNELS.businessConfigSave, (payload) => creds.businessSave((payload ?? {}) as Partial<{ businessName: string; rut: string; ivaPercent: number; currency: string; mlUserId: string }>));
  transport.on(IPC_CHANNELS.mlAccount, () => creds.mlAccount());
  transport.on(IPC_CHANNELS.mlDataSpike, () => {
    if (!creds.status().mercadolibre.configured) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", probes: [] };
    return {
      ok: true, detail: "Diagnóstico completo (demostración).",
      probes: [
        { name: "/users/me (cuenta + token)", ok: true, detail: "id 123456" },
        { name: "/items/{id} → sold_quantity", ok: true, detail: "sold_quantity=17 ✓ (¡clave!)" },
        { name: "/trends/MLC (búsquedas top)", ok: true, detail: "20 términos" },
        { name: "/highlights (más vendidos)", ok: false, detail: "HTTP 403 (¿app no certificada?)" },
        { name: "/reviews/item/{id}", ok: false, detail: "HTTP 404" },
      ],
    };
  });
  transport.on(IPC_CHANNELS.trendCategories, () => {
    if (!creds.status().mercadolibre.configured) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", categories: [] };
    return { ok: true, detail: "5 categorías (demostración)", categories: [
      { id: "MLC1500", name: "Herramientas" }, { id: "MLC1276", name: "Deportes y Fitness" },
      { id: "MLC1051", name: "Celulares y Teléfonos" }, { id: "MLC1574", name: "Hogar y Muebles" }, { id: "MLC1132", name: "Juguetes" },
    ] };
  });
  transport.on(IPC_CHANNELS.trendSubcategories, () => ({ ok: true, detail: "3 subcategorías (demostración)", categories: [
    { id: "MLC1501", name: "Herramientas Eléctricas" }, { id: "MLC1502", name: "Herramientas Manuales" }, { id: "MLC1503", name: "Accesorios" },
  ] }));
  transport.on(IPC_CHANNELS.trendBestSellers, () => {
    if (!creds.status().mercadolibre.configured) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", categoryId: "", items: [] };
    return { ok: true, detail: "5 más vendidos (demostración)", categoryId: "MLC1500", items: [
      { position: 1, itemId: "MLC-DEMO-1", title: "Taladro percutor inalámbrico 20V (demostración)", priceMinor: 44990, soldQuantity: 0, sellers: 18, sellerId: "111", condition: "new", permalink: "" },
      { position: 2, itemId: "MLC-DEMO-2", title: "Set destornilladores 32 piezas", priceMinor: 12990, soldQuantity: 0, sellers: 7, sellerId: "222", condition: "new", permalink: "" },
      { position: 3, itemId: "MLC-DEMO-3", title: "Amoladora angular 4.5\" 820W", priceMinor: 29990, soldQuantity: 0, sellers: 24, sellerId: "111", condition: "new", permalink: "" },
      { position: 4, itemId: "MLC-DEMO-4", title: "Nivel láser autonivelante", priceMinor: 39990, soldQuantity: 0, sellers: 4, sellerId: "333", condition: "new", permalink: "" },
      { position: 5, itemId: "MLC-DEMO-5", title: "Caja de herramientas metálica", priceMinor: 24990, soldQuantity: 0, sellers: 11, sellerId: "444", condition: "new", permalink: "" },
    ] };
  });
  transport.on(IPC_CHANNELS.trendAnalyze, () => {
    if (!creds.status().mercadolibre.configured) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", categoryId: "", capturedDays: 0, comparedTo: "", weakSpots: 0, items: [] };
    // Demostración con Δ posición (momentum, Fase 3) + señales "día 1" (Fase 4).
    return { ok: true, detail: "5 más vendidos (demostración)", categoryId: "MLC1500", capturedDays: 2, comparedTo: "2026-08-17", weakSpots: 3, items: [
      { position: 1, itemId: "MLC-DEMO-1", title: "Taladro percutor inalámbrico 20V (demostración)", priceMinor: 44990, soldQuantity: 0, sellers: 18, sellerId: "111", condition: "new", permalink: "", prevPosition: 3, deltaPosition: 2, deltaPriceMinor: -2000, isNew: false, weakSpot: true, opportunityHint: "Se vende mucho a precio alto: puedes competir por precio.", demandMomentum: 100, entryOpportunity: 60, tier: "alta", classification: { kind: "en-auge", label: "En auge", note: "Alta demanda y subiendo — momento caliente para entrar o competir." }, signals: [{ kind: "demand", label: "Top demanda (#1)", tone: "info" }, { kind: "price-high", label: "Precio alto: margen para entrar más barato", tone: "opportunity" }] },
      { position: 2, itemId: "MLC-DEMO-2", title: "Set destornilladores 32 piezas", priceMinor: 12990, soldQuantity: 0, sellers: 7, sellerId: "222", condition: "new", permalink: "", prevPosition: 2, deltaPosition: 0, deltaPriceMinor: 0, isNew: false, weakSpot: false, demandMomentum: 80, entryOpportunity: 32, tier: "media", classification: { kind: "maduro", label: "Maduro", note: "Demanda estable — mercado asentado, compite por precio y servicio." }, signals: [{ kind: "demand", label: "Top demanda (#2)", tone: "info" }, { kind: "price-low", label: "Precio muy bajo: margen ajustado", tone: "warning" }] },
      { position: 3, itemId: "MLC-DEMO-6", title: "Pistola de calor 2000W (nuevo en ranking)", priceMinor: 19990, soldQuantity: 0, sellers: 5, sellerId: "555", condition: "new", permalink: "", isNew: true, weakSpot: true, opportunityHint: "Se vende mucho y hay pocos vendedores: hueco de oferta.", demandMomentum: 75, entryOpportunity: 26, tier: "media", classification: { kind: "emergente", label: "Emergente", note: "Entra con tracción — oportunidad temprana, vigílalo de cerca." }, signals: [{ kind: "demand", label: "Top demanda (#3)", tone: "info" }, { kind: "competition-low", label: "Poca competencia (5 vendedores)", tone: "opportunity" }] },
      { position: 4, itemId: "MLC-DEMO-4", title: "Nivel láser autonivelante", priceMinor: 39990, soldQuantity: 0, sellers: 4, sellerId: "333", condition: "new", permalink: "", prevPosition: 1, deltaPosition: -3, deltaPriceMinor: 3000, isNew: false, weakSpot: true, opportunityHint: "Alta demanda con poca competencia y precio alto: la mejor ventana para entrar.", demandMomentum: 16, entryOpportunity: 33, tier: "baja", classification: { kind: "en-declive", label: "En declive", note: "Perdiendo posiciones — la demanda se está enfriando." }, signals: [{ kind: "demand", label: "Top demanda (#4)", tone: "info" }, { kind: "competition-low", label: "Poca competencia (4 vendedores)", tone: "opportunity" }, { kind: "price-high", label: "Precio alto: margen para entrar más barato", tone: "opportunity" }] },
      { position: 5, itemId: "MLC-DEMO-5", title: "Caja de herramientas metálica", priceMinor: 24990, soldQuantity: 0, sellers: 11, sellerId: "444", condition: "new", permalink: "", prevPosition: 5, deltaPosition: 0, deltaPriceMinor: 0, isNew: false, weakSpot: false, demandMomentum: 20, entryOpportunity: 8, tier: "baja", classification: { kind: "maduro", label: "Maduro", note: "Demanda estable — mercado asentado, compite por precio y servicio." }, signals: [{ kind: "demand", label: "Top demanda (#5)", tone: "info" }] },
    ] };
  });
  transport.on(IPC_CHANNELS.trendCapture, () => {
    if (!creds.status().mercadolibre.configured) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", day: "", captured: 0, totalDays: 0 };
    return { ok: true, detail: "Foto guardada (5 productos). Histórico: 2 día(s). (demostración)", day: "2026-08-18", captured: 5, totalDays: 2 };
  });
  transport.on(IPC_CHANNELS.trendTracked, () => ({ items: [{ categoryId: "MLC1500", days: 3, lastDay: "2026-08-18" }] }));
  transport.on(IPC_CHANNELS.trendHistory, () => {
    if (!creds.status().mercadolibre.configured) return { categoryId: "", days: [], items: [] };
    const days = ["2026-08-16", "2026-08-17", "2026-08-18"];
    // A escala del ranking: A escala fuerte (5→3→1), B estable, C cae (2→4→5).
    return { categoryId: "MLC1500", days, items: [
      { itemId: "MLC-DEMO-1", title: "Taladro percutor inalámbrico 20V (demostración)", lastPosition: 1, lastPriceMinor: 44990,
        points: [{ day: days[0], position: 5, priceMinor: 46990, sellers: 20 }, { day: days[1], position: 3, priceMinor: 45990, sellers: 19 }, { day: days[2], position: 1, priceMinor: 44990, sellers: 18 }],
        velocity: { daysTracked: 3, firstDay: days[0], lastDay: days[2], positionSlope: -2, positionVelocity: 2, netPositionChange: 4, priceSlopeMinor: -1000, priceChangePct: -4.26, trend: "subiendo" } },
      { itemId: "MLC-DEMO-2", title: "Set destornilladores 32 piezas", lastPosition: 2, lastPriceMinor: 12990,
        points: [{ day: days[0], position: 2, priceMinor: 12990, sellers: 7 }, { day: days[1], position: 2, priceMinor: 12990, sellers: 7 }, { day: days[2], position: 2, priceMinor: 12990, sellers: 7 }],
        velocity: { daysTracked: 3, firstDay: days[0], lastDay: days[2], positionSlope: 0, positionVelocity: 0, netPositionChange: 0, priceSlopeMinor: 0, priceChangePct: 0, trend: "estable" } },
      { itemId: "MLC-DEMO-4", title: "Nivel láser autonivelante", lastPosition: 5, lastPriceMinor: 39990,
        points: [{ day: days[0], position: 2, priceMinor: 36990, sellers: 4 }, { day: days[1], position: 4, priceMinor: 38990, sellers: 4 }, { day: days[2], position: 5, priceMinor: 39990, sellers: 4 }],
        velocity: { daysTracked: 3, firstDay: days[0], lastDay: days[2], positionSlope: 1.5, positionVelocity: -1.5, netPositionChange: -3, priceSlopeMinor: 1500, priceChangePct: 8.11, trend: "bajando" } },
    ] };
  });
  transport.on(IPC_CHANNELS.trendReviews, (payload) => {
    if (!creds.status().mercadolibre.configured) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", itemId: "", title: "", report: { reviewsTotal: 0, avgRating: 0, negativeShare: 0, withText: 0, negativeCount: 0, distribution: null, questionsTotal: 0, complaints: [], doubts: [], gaps: [], verdict: "Sin datos." } };
    const p = payload as { itemId?: string; title?: string } | undefined;
    return { ok: true, detail: "58 opinión(es) · 12 con texto (demostración)", itemId: p?.itemId ?? "MLC-DEMO-1", title: p?.title ?? "Taladro percutor inalámbrico 20V",
      report: {
        reviewsTotal: 58, avgRating: 4.1, negativeShare: 24, withText: 12, negativeCount: 14,
        distribution: { one: 8, two: 6, three: 5, four: 12, five: 27 },
        questionsTotal: 0,
        complaints: [
          { id: "bateria", label: "Batería / autonomía", count: 5, sample: "La batería dura poco, se descarga muy rápido." },
          { id: "calidad", label: "Calidad / durabilidad", count: 3, sample: "Se rompió el mandril a la semana, mala calidad." },
          { id: "manual", label: "Instrucciones / idioma", count: 2, sample: "El manual viene en inglés, no se entiende." },
        ],
        doubts: [],
        gaps: [
          { id: "bateria", label: "Batería / autonomía", count: 10, suggestion: "Elige un modelo con mayor autonomía y decláralo en el título." },
          { id: "calidad", label: "Calidad / durabilidad", count: 6, suggestion: "Ofrece una versión más robusta y con garantía visible." },
          { id: "manual", label: "Instrucciones / idioma", count: 4, suggestion: "Incluye manual claro en español y video de armado." },
        ],
        verdict: "Principal hueco: batería / autonomía (10 señales). Elige un modelo con mayor autonomía y decláralo en el título.",
      } };
  });
  // Vigilancia (Fase 12): watchlist en memoria para la demostración.
  const watch = new Map<string, { categoryId: string; name: string; addedAt: number }>();
  transport.on(IPC_CHANNELS.trendWatchlist, () => ({ items: [...watch.values()] }));
  transport.on(IPC_CHANNELS.trendWatchAdd, (payload) => {
    const p = payload as { categoryId?: string; name?: string } | undefined;
    const id = (p?.categoryId ?? "").trim();
    if (id && !watch.has(id)) watch.set(id, { categoryId: id, name: p?.name || id, addedAt: Date.now() });
    return { items: [...watch.values()] };
  });
  transport.on(IPC_CHANNELS.trendWatchRemove, (payload) => {
    watch.delete(((payload as { categoryId?: string } | undefined)?.categoryId ?? "").trim());
    return { items: [...watch.values()] };
  });
  transport.on(IPC_CHANNELS.trendWatchRun, () => {
    if (!creds.status().mercadolibre.configured) return { ok: false, detail: "Configura las credenciales de Mercado Libre en Seguridad", ranAt: Date.now(), categories: watch.size, captured: 0, newFindings: [] };
    const findings = watch.size === 0 ? [] : [
      { kind: "en-auge", title: "Taladro percutor inalámbrico 20V", detail: "En auge (demanda 100/100) en el ranking.", categoryName: [...watch.values()][0]?.name ?? "" },
      { kind: "market-gap", title: "Nivel láser autonivelante", detail: "Hueco: Batería / autonomía. Elige un modelo con mayor autonomía.", categoryName: [...watch.values()][0]?.name ?? "" },
    ];
    return { ok: true, detail: findings.length > 0 ? `${findings.length} novedad(es) en ${watch.size} categoría(s) vigilada(s). (demostración)` : `Vigilancia al día: sin novedades en ${watch.size} categoría(s).`, ranAt: Date.now(), categories: watch.size, captured: watch.size, newFindings: findings };
  });
  transport.on(IPC_CHANNELS.mlQuestionsList, () => creds.mlQuestions());
  transport.on(IPC_CHANNELS.mlQuestionsDraft, () => creds.draftQuestions());
  transport.on(IPC_CHANNELS.mlQuestionAnswer, (payload) => creds.answerQuestion((payload ?? {}) as { questionId?: string; text?: string }));
  transport.on(IPC_CHANNELS.aiAsk, (payload) => {
    // Item 4: el asistente responde con un snapshot real del negocio (derivado de los mock stores).
    const prompt = (payload as { prompt?: string } | undefined)?.prompt ?? "";
    const st = creds.status();
    if (!st.ai.configured) return { ok: false, answer: "Configura tu API key de IA en Seguridad.", provider: st.ai.extra ?? "openai" };
    const inv = mockInventory.overview();
    const sinStock = inv.rows.filter((r) => r.status.label === "Sin stock").map((r) => r.primary);
    const bajo = inv.rows.filter((r) => r.status.label === "Stock bajo").map((r) => r.primary);
    const sales = mockSales.overview();
    const fin = mockFinance.overview();
    const val = (ov: EngineOverviewDto, label: string): string => ov.metrics.find((m) => m.label.includes(label))?.value ?? "—";
    const reco = sinStock.length ? `Prioridad: repón ${sinStock.join(", ")} (sin stock). ` : bajo.length ? `Atención: ${bajo.join(", ")} con stock bajo. ` : "Tu stock está sano. ";
    const answer = `(demostración con tus datos reales) ${reco}Ventas confirmadas: ${val(sales, "Confirmadas")}, ingreso ${val(sales, "Ingreso")}. Flujo neto: ${val(fin, "Flujo")}. Sobre "${prompt}": con tu API key real, ${st.ai.extra ?? "openai"} respondería usando este mismo contexto.`;
    return { ok: true, answer, provider: st.ai.extra ?? "openai" };
  });
  transport.on(IPC_CHANNELS.brainAiRecommendation, () => {
    // Item 5: recomendación IA para el Dashboard, fundamentada en el snapshot real del negocio.
    const st = creds.status();
    if (!st.ai.configured) return { ok: false, answer: "Configura tu API key de IA en Seguridad.", provider: "—" };
    const inv = mockInventory.overview();
    const sinStock = inv.rows.filter((r) => r.status.label === "Sin stock").map((r) => r.primary);
    const bajo = inv.rows.filter((r) => r.status.label === "Stock bajo").map((r) => r.primary);
    const sales = mockSales.overview();
    const val = (ov: EngineOverviewDto, label: string): string => ov.metrics.find((m) => m.label.includes(label))?.value ?? "—";
    const action = sinStock.length
      ? `Repón hoy ${sinStock.join(", ")}: están sin stock y frenan ventas.`
      : bajo.length
        ? `Programa reposición de ${bajo.join(", ")} (stock bajo) antes del quiebre.`
        : `Tu stock está sano; empuja las ${val(sales, "Confirmadas")} ventas confirmadas con más publicaciones activas.`;
    return { ok: true, answer: `(demostración con tus datos reales) ${action}`, provider: st.ai.extra ?? "openai" };
  });
  // Respaldo simulado (dev/navegador): lista en memoria, sin archivos reales.
  const backups: { name: string; size: number; at: number }[] = [];
  transport.on(IPC_CHANNELS.backupCreate, () => {
    const item = { name: `fabianmeli-${new Date().toISOString().replace(/[:.]/g, "-")}.db`, size: 20480, at: Date.now() };
    backups.unshift(item);
    return item;
  });
  transport.on(IPC_CHANNELS.backupList, () => ({ items: [...backups] }));
  transport.on(IPC_CHANNELS.backupRestore, (payload) => ({ restored: (payload as { name?: string } | undefined)?.name ?? "" }));
  // Export/import (item 4): en la app real abren un diálogo nativo; aquí se simulan.
  transport.on(IPC_CHANNELS.backupExport, () => {
    const name = `fabianmeli-export-${new Date().toISOString().replace(/[:.]/g, "-")}.db`;
    return { path: `(demostración) C:/Users/tu-usuario/Documentos/${name}`, name, size: 20480, at: Date.now() };
  });
  transport.on(IPC_CHANNELS.backupImport, () => ({ restored: "(demostración) archivo-importado.db" }));
  // Centro de avisos simulado (item 5): un aviso de ejemplo sembrado para demostración.
  let notices: { id: string; level: "info" | "warning" | "error"; title: string; message: string; at: number; read: boolean }[] = [
    { id: "demo-1", level: "warning", title: "Sincronización con Mercado Libre", message: "Ejemplo: aquí verás los fallos de fondo (respaldo, sincronización) que ocurran.", at: Date.now() - 3600_000, read: false },
  ];
  const notifSnapshot = (): { items: typeof notices; unread: number } => ({ items: [...notices], unread: notices.filter((n) => !n.read).length });
  transport.on(IPC_CHANNELS.notificationsList, () => notifSnapshot());
  transport.on(IPC_CHANNELS.notificationsMarkAllRead, () => { notices = notices.map((n) => ({ ...n, read: true })); return notifSnapshot(); });
  transport.on(IPC_CHANNELS.notificationsDismiss, (payload) => { const id = (payload as { id?: string } | undefined)?.id; notices = notices.filter((n) => n.id !== id); return notifSnapshot(); });
  transport.on(IPC_CHANNELS.notificationsClear, () => { notices = []; return notifSnapshot(); });
  // Generador de imágenes de publicación (simulado en navegador).
  transport.on(IPC_CHANNELS.productImagesAnalyze, () => creds.analyzeImage());
  transport.on(IPC_CHANNELS.productImagesGenerate, (payload) => creds.generateImage((payload as { prompt?: string } | undefined)?.prompt ?? ""));
  transport.on(IPC_CHANNELS.productDescriptionGenerate, (payload) => creds.generateDescription((payload ?? {}) as { title?: string; context?: string }));
  transport.on(IPC_CHANNELS.exportSaveImage, (payload) => {
    const name = (payload as { defaultName?: string } | undefined)?.defaultName ?? "imagen.png";
    return { path: `(demostración) C:/Users/tu-usuario/Imágenes/${name}`, name };
  });
  // Exportación CSV (item 7): en la app real abre un diálogo nativo; aquí se simula la ruta.
  transport.on(IPC_CHANNELS.exportSave, (payload) => {
    const name = (payload as { defaultName?: string } | undefined)?.defaultName ?? "informe.csv";
    return { path: `(demostración) C:/Users/tu-usuario/Documentos/${name}`, name };
  });
  // Auto-actualización (item 10): en navegador no hay updater; se reporta como no configurado.
  const updateState = { status: "disabled" as const, version: "0.1.0", configured: false, detail: "Auto-actualización disponible en la app instalada (requiere servidor de updates)." };
  transport.on(IPC_CHANNELS.updateStatus, () => ({ ...updateState }));
  transport.on(IPC_CHANNELS.updateCheck, () => ({ ...updateState }));
  transport.on(IPC_CHANNELS.updateInstall, () => ({ installing: false }));
  return transport;
}
